/**
 * Shared geometry + materialization helpers for the manual cabinet-run tools
 * (the draw tool and the appliance-drop flow). One source of truth so the two
 * paths can't drift.
 */

import {
  defaultSidedness,
  freeSegIndexOf,
  synthesizeVirtualWall,
  pointToSegmentDistance,
  overridesForRun,
  isReseatedTallUnit,
  clampApplianceCenter,
  cornerIndexForUnit,
  buildApplianceObjects,
  familyById,
  familyFieldsForNewRun,
  familyOf,
  isUnknownFamily,
  isTallOverride,
  narrowestModuleIn,
  uppersEnabled,
} from '@shared-lib/cabinet-runs';
import {
  worldToWallParam,
  getWallAxes,
} from '@shared-lib/kitchen-assembly/wallMath';
import { INCHES_TO_METERS } from '@shared-lib/kitchen-assembly/constants';
import {
  upperBandFor,
  statedUpperSpec,
  statedStack,
  scannedUpperSpec,
  hoodChimneyTopY,
} from '@shared-lib/cabinet-runs/upperBand';
import { buildSegmentWallResolver } from '@shared-lib/cabinet-runs/segmentWall';
import { WALL_SNAP_REACH } from '@shared-lib/cabinet-runs/buildRunSegments';
import { fridgeColumnLineY } from '@shared-lib/cabinet-runs/fridgeColumn';
import {
  roomCeilingY,
  groupOtherCabinetRuns,
  runUpperBand,
} from '@shared-lib/cabinet-runs/materializeRun';
import {
  bayHeightFor,
  fitsUnderCeiling,
  roomInches,
  runUpperHeightIn,
} from '@shared-lib/cabinet-runs/upperHeights';
import {
  glassUnavailableReason,
  keepGlass,
  lookDoorStyleOf,
} from '@shared-lib/cabinet-runs/glassUppers';
import { effectiveAppearance } from '@shared-lib/cabinet-runs/bayOverrides';
import { LOOK_KEYS, sameLook } from '@shared-lib/cabinet-runs/newRunFamily';
import {
  normalizeRollOuts,
  rollOutRoomFor,
  rollOutUnavailableReason,
} from '@shared-lib/cabinet-runs/rollOuts';
import { mergeResolvedRuns } from '@shared-lib/cabinet-runs/mergeResolvedRuns';
import {
  vanityToRun,
  canConvertVanityToRun,
} from '@shared-lib/cabinet-runs/vanityToRun';
import { sizingOf, AS_BUILT, STANDARD } from '@shared-lib/cabinet-runs/sizing';
import {
  vanitySinkBays,
  bowlsOfSinkBay,
} from '@shared-lib/cabinet-runs/vanitySinkBay';
import {
  VANITY_PROFILE_ID,
  profileOf,
  depthInLine,
  depthOptionsForRun,
} from '@shared-lib/cabinet-runs/profiles';
import { objectsAfterRunDelete } from '@shared-lib/cabinet-runs/deleteRun';
import {
  findRunByIdOrAlias,
  runAnswersTo,
} from '@shared-lib/cabinet-runs/runIds';
import { RESOLVE_SITES } from '@shared-lib/cabinet-runs/resolvePolicy';
import { isAutoRun, isDerivedRun } from '@shared-lib/cabinet-runs/runSource';
import { userChoiceField } from '@shared-lib/cabinet-runs/cornerChoice';
import { healPersistedRuns } from './runFrameMigration';
import { GENERATOR_VERSION } from '@shared-lib/kitchen-assembly/cabinetLayout/schema';
import { getFloorY } from '@shared-lib/vanity-assembly/placementUtils';
import { isVanityAssemblyGroupId } from '@shared-lib/vanity-assembly/fixtureGroup';
import { runDepthSpec } from '@shared-lib/cabinet-runs/profiles';
import {
  baysForRun,
  isApplianceKind,
  storedRunFromRun,
} from '@shared-lib/cabinet-runs/bays';
import {
  offeringOf,
  lookForFamily,
  lookNotOffered,
  overlayLook,
} from '@shared-lib/cabinet-runs/familyOffering';
import { isDoorlessCabinet } from '@shared-lib/cabinet-runs/doorStyles';
import { familySizes } from '@shared-lib/cabinet-runs/vendorSizes';
import {
  normalizeSinkDrawers,
  sinkUnitChoices,
} from '@shared-lib/cabinet-runs/sinkUnits';
import {
  snapBayWidthIn,
  standInBoxFor,
} from '@shared-lib/cabinet-runs/bayWidths';
import {
  cornerSizesInLine,
  runHoldsUnitsOutsideLine,
  runUnitsIntoLine,
  tallUnitsInLine,
} from '@shared-lib/cabinet-runs/keptUnitsInLine';
import { rebaseRunFrames } from '@shared-lib/cabinet-runs/wallFrame';
import { resolveGroupsAfterMove } from './resolveGroupsAfterMove';
import { isUpperCabinetLikeObject } from './fixtureTypeUtils';
import {
  VALANCE_STYLES,
  sinkBayUnder,
} from '@shared-lib/cabinet-runs/valanceBays';
import { glassHolderReason } from '@shared-lib/cabinet-runs/glassHolders';
import {
  RACK_CABINET_POSITIONS,
  RACK_CABINET_FRONTS,
} from '@shared-lib/cabinet-runs/rackStacks';
import {
  isStaleDerivedRun,
  rehydrateRun,
  storedRunLegSides,
  flipStoredRunSide,
  stampStoredRun,
  runEndsForRoom,
  getRunOpenings,
  openingsForRun,
  materializeRunObjects,
  runBayForObject,
  applianceSizeChanged,
  runForInstallPiece,
  resolveRunAfterObjectChange,
  storedRunFor,
  reportMergeDiagnostics,
  stampRunReadback,
  withVanityRunAccessories,
  objectsAfterRunResolve,
} from '@shared-lib/cabinet-runs/editResolve';

// Provenance predicates now live in shared-lib; re-exported here because every
// web caller reaches them through this module.
export { isAutoRun, isDerivedRun };

// The EDIT site's re-solve of one stored run lives in shared-lib
// (`cabinet-runs/editResolve.js`) so the server re-solves a run exactly as
// the browser does; re-exported here because every web caller reaches it
// through this module.
export {
  isStaleDerivedRun,
  rehydrateRun,
  storedRunLegSides,
  flipStoredRunSide,
  stampStoredRun,
  runEndsForRoom,
  getRunOpenings,
  openingsForRun,
  materializeRunObjects,
  runBayForObject,
  applianceSizeChanged,
  runForInstallPiece,
  resolveRunAfterObjectChange,
  withVanityRunAccessories,
  objectsAfterRunResolve,
};

const PARAM_EPS = 1e-6;

/**
 * Which TIER of the run an edited box belongs to.
 *
 * An upper and the lower beneath it share a wall param (and a corner upper
 * shares its corner lower's `cornerIndex`), so an override keyed by position
 * alone cannot say which of the two it means — a Unit-scope edit on an upper
 * used to land on the lower under it. Read through `isUpperCabinetLikeObject`
 * rather than off `type`: a CORNER upper is `type: 'corner-cabinet'` and is
 * told apart from the corner lower by elevation, which no type table can say.
 */
const bayTier = (bay) => (isUpperCabinetLikeObject(bay) ? 'upper' : 'lower');

/** An override's tier; absent means the lower bay (every pre-tier record). */
const overrideTier = (o) => o?.tier ?? 'lower';

// A click must be this close to a wall to land on it — the draw tool's one
// reach, shared with the corner and off-wall rules (`buildRunSegments`).
export const SNAP_TOLERANCE = WALL_SNAP_REACH;

/**
 * Whether a wall move may re-solve this run.
 *
 * A HAND-DRAWN run does (spec §8.5): its segments name room walls like every
 * other run's, and re-solving against the moved wall — keeping the extent the
 * user drew, clamped to the wall (ruling A) — is what stops a moved wall from
 * orphaning the run and the next panel edit deleting it.
 *
 * A GENERATED run always does — its geometry is generator output.
 *
 * A SCAN run does only once the user has edited it: `processStructure` derives
 * scan runs for every scanned kitchen, so the user never asked for one, and
 * re-laying-out MEASURED cabinetry because they nudged a wall would destroy
 * the scan's record of the room. Once edited (`userEdited`) it is theirs.
 */
export function regeneratesOnWallMove(run) {
  if (isAutoRun(run)) return true;
  if (run?.source === 'scan') return !!run.userEdited;
  // Neither generated nor scanned → hand-drawn, whatever `source` says: the
  // spec makes `source` provenance only, so no value of it means "manual".
  return true;
}

/**
 * The cabinet-wall id a bay lives on. Manual/re-solved objects carry
 * `parentWallIds`; freshly-generated AUTO objects carry `cabinetWallRefs`
 * (fromPhaseB deletes parentWallIds), so read either.
 */
function bayWallId(bay) {
  return bay?.parentWallIds?.[0] || bay?.cabinetWallRefs?.[0]?.id || null;
}

/** Wall-param of a point for a bay's run. */
function runWallParamForPoint(stored, wallId, point, room) {
  const seg = (stored.segments || []).find((s) => s.wallId === wallId);
  const ew = buildSegmentWallResolver(room).resolve(seg || { wallId });
  return ew ? worldToWallParam(ew, point) : null;
}

// Below this, a run's extent counts as "the whole wall" and a wall move may
// refresh it to the new full length. 1 cm: narrower than any real reservation.
const PARTIAL_EXTENT_EPS = 0.01;

/**
 * Standalone scene objects for an appliance just dropped onto a DERIVED run
 * (auto / scan): the appliance box plus its install pieces (a range's vent hood;
 * a fridge's surround + over-fridge cabinet). Derived runs RESERVE the appliance
 * footprint — the cabinets reflow around the anchor in `materializeRunObjects` —
 * but they do NOT own or re-emit the appliance object itself (the auto re-solve
 * drops `appliancePlacements`). So we emit it ONCE here, UNTAGGED, with the same
 * ownership a generated kitchen's appliances have (materializeCandidate-owned):
 * a later run re-solve (bay edit / regen-merge) neither deletes nor re-emits it,
 * so it can't double up.
 *
 * Non-sink only: a sink couples its basin + faucet to the run countertop
 * (deferred). Returns [] for manual runs (they own + emit their own appliances
 * via materializeRunObjects) and for a sink or unknown appliance id. A
 * fridge unit's top and a hood's are the run's own lines, read off its band.
 */
export function buildDroppedApplianceObjects(
  run,
  room,
  openings = [],
  applianceId
) {
  // A run of a family this build does not know is never rebuilt
  // (`isUnknownFamily`), and its install pieces would be the default family's.
  if (!isDerivedRun(run) || isUnknownFamily(run)) return [];
  const appliance = (run.appliances || []).find((a) => a.id === applianceId);
  if (!appliance || appliance.type === 'sink') return [];

  const floorY = getFloorY(room);
  // A dropped range's hood and a dropped fridge's column take the run's lines
  // from its BAND, exactly as fillRun does (plan 2026-10-21, C22): the hood
  // the uppers' own line, the column a stacked row's top where there is one
  // (`fridgeColumnLineY`). Uppers OFF is the "no upper line" case, and only
  // there does the unit run to the ceiling: a run with uppers ON that emitted
  // none still HAS a line, and a tall scribe beside the fridge is already
  // standing on it (`fridgeColumnSpec` in `fillRun.js`). The max of the
  // reflowed uppers was the same number until a stacked row stood on them,
  // and then it took the hood up to the stack's top.
  const ceilingY = roomCeilingY(room, floorY);
  const band = uppersEnabled(run)
    ? upperBandFor(run, { floorY, ceilingY })
    : null;
  const ctx = {
    floorY,
    ceilingY,
    ceilingId: room?.ceilings?.[0]?.id || null,
    roomAppearance: room.cabinetAppearance,
    openings: openingsForRun(run, room, openings),
    ...(band
      ? {
          upperTopY: band.topY,
          columnTopY: fridgeColumnLineY(run, band, floorY),
          hoodChimneyTopY: hoodChimneyTopY(band),
        }
      : {}),
  };

  // Solo run — only the dropped appliance, so buildApplianceObjects emits just
  // its pieces. Segments are kept as-is so the appliance's segmentIndex still
  // resolves; the run is already rehydrated, so its segments carry `.wall`.
  const soloRun = { ...run, appliances: [appliance], bays: undefined };
  const { objects } = buildApplianceObjects(soloRun, ctx);
  const bayId = `bay_${appliance.id}`;

  // Untag + re-provenance: standalone, NOT run-owned. Strip runId so a later run
  // re-solve can't delete them, and stamp a distinct addedBy so they read as an
  // auto-kitchen appliance drop.
  return objects.map((o) => {
    const rest = { ...o, bayId };
    delete rest.runId;
    rest.addedBy = 'cabinet-appliance-drop';
    rest.layoutProvenance = {
      layoutVersion: GENERATOR_VERSION,
      sourceKey: 'userEdited',
    };
    return rest;
  });
}

/**
 * The valid drop position for an appliance of `type` nearest the cursor's
 * `rawCenter` on `segmentIndex` of `storedRun` — slid clear of same-wall
 * openings it would block, the run's ends, and existing appliances (the same
 * feel as the corner clamp). Returns the clamped wall-param, or `null` when the
 * appliance can't fit anywhere clear on the segment. `storedRun` must NOT
 * already contain the candidate appliance.
 *
 * Bounded by the segment's fill zone (`segmentZoneBounds`), read off
 * `fillOf` — the run as it stands. A bay MOVE clamps a copy without the bay
 * being moved, and passes the real run here, so every frame of a drag reads
 * the same memoised fill instead of a new copy's.
 *
 * Nearest clear place to the pointer, for a drop and a drag alike: a bay
 * pushed into a neighbour stops flush against it, and HOPS it once the
 * pointer is past the neighbour's middle — only if a clear gap is waiting on
 * the far side (user ruling 2026-10-26).
 */
export function clampApplianceCenterParam(
  storedRun,
  segmentIndex,
  type,
  rawCenter,
  room,
  openings = [],
  { fillOf = storedRun } = {}
) {
  const run = rehydrateRun(storedRun, room);
  const ctx = {
    floorY: getFloorY(room),
    openings: openingsForRun(storedRun, room, openings),
  };
  const free = freeSegmentAsRun(run, storedRun, segmentIndex, room);
  if (free) return clampApplianceCenter(free, 0, type, rawCenter, ctx);
  const fillBounds = segmentZoneBounds(fillOf, segmentIndex, room, openings);
  return clampApplianceCenter(
    run,
    segmentIndex,
    type,
    rawCenter,
    fillBounds ? { ...ctx, fillBounds } : ctx
  );
}

/** The last fill each stored run was read through. */
const fillMemo = new WeakMap();

/**
 * The run's fill, memoised on the stored run and the room — which hold still
 * for a whole drag, so a gesture pays for one fill rather than one per frame.
 */
function memoisedFill(storedRun, room, openings) {
  let hit = fillMemo.get(storedRun);
  if (!hit || hit.room !== room) {
    hit = {
      room,
      resolved: materializeRunObjects(
        rehydrateRun(storedRun, room),
        room,
        openings
      ),
    };
    fillMemo.set(storedRun, hit);
  }
  return hit.resolved;
}

/**
 * The span the fill packs on wall-backed segment `segmentIndex` — its zone,
 * between the corners as the run BUILT them — or null when the fill packed
 * nothing there (the clamp then keeps its default corner allowance).
 *
 * Read off a fill because only the fill decides a corner: a blind corner
 * standing on the other wall takes that leg's depth from this one, an L its
 * whole box.
 */
export function segmentZoneBounds(storedRun, segmentIndex, room, openings) {
  if (!storedRun || !room) return null;
  const zones = memoisedFill(storedRun, room, openings)?.zones || [];
  const zone = zones.find((z) => z.segIndex === segmentIndex);
  return zone ? { lo: zone.lo, hi: zone.hi } : null;
}

/**
 * How far along its segment a bay's CENTRE may sit: the segment's fill bounds
 * inset by half the bay's own width — a bay may never hang off the end of its
 * own run. `{lo, hi}`, or null when the segment cannot be resolved.
 *
 * This is the bound of last resort. `clampApplianceCenterParam` normally
 * answers with a richer one (clear of openings and of the other bays), but it
 * returns null when the appliance fits NOWHERE clear — a 27" sink on a 60"
 * island that also holds a dishwasher — and the caller then had nothing to
 * hold on to at all. On a wall the wall's own end still stopped the arrows;
 * an island has no end to stop them, so a drag could take the sink clean off
 * its island.
 *
 * A segment shorter than the bay collapses to its midpoint rather than an
 * inverted range.
 */
export function bayCenterSpan(room, storedRun, segIndex, bay) {
  const free = freeSegmentAsRun(
    rehydrateRun(storedRun, room),
    storedRun,
    segIndex,
    room
  );
  const seg = free
    ? free.segments[0]
    : rehydrateRun(storedRun, room).segments?.[segIndex];
  if (
    !seg ||
    !Number.isFinite(seg.startParam) ||
    !Number.isFinite(seg.endParam)
  )
    return null;
  const lo = Math.min(seg.startParam, seg.endParam);
  const hi = Math.max(seg.startParam, seg.endParam);
  const half = (bay?.width || 0) / 2;
  if (hi - lo <= 2 * half) {
    const mid = (lo + hi) / 2;
    return { lo: mid, hi: mid };
  }
  return { lo: lo + half, hi: hi - half };
}

/**
 * A FREE-STANDING leg as the single-segment run the clamp can read, or null
 * when `segmentIndex` is wall-backed.
 *
 * The clamp bounds a bay by `segment.startParam`/`endParam` and by the OTHER
 * bays on the same segment index. A rehydrated free leg carries neither: it
 * has two world endpoints and no wall, and its bays are indexed against the
 * run. So it is rebuilt exactly as `materializeRun.fillFreestandingSegments`
 * rebuilds it before filling — the leg's virtual wall, `0..lineLength`, and
 * that leg's bays reindexed to segment 0 — and the clamp reads the same
 * bounds the fill will pack to. Without this an island bay had no bounds at
 * all: `clampApplianceCenter` returned null on the NaN span and the caller
 * fell back to the raw target, so a dragged bay would run off its own island.
 */
function freeSegmentAsRun(run, storedRun, segmentIndex, room) {
  if (!storedRun?.segments?.[segmentIndex]?.freestanding) return null;
  const resolved = resolveFreeSegment(storedRun, segmentIndex, room);
  if (!resolved) return null;
  return {
    ...run,
    segments: [
      { wall: resolved.wall, startParam: 0, endParam: resolved.lineLength },
    ],
    bays: baysForRun(run)
      .filter((b) => (b.segmentIndex ?? 0) === segmentIndex)
      .map((b) => ({ ...b, segmentIndex: 0 })),
    bayOverrides: (run.bayOverrides || [])
      .filter((o) => o.segmentIndex === segmentIndex)
      .map((o) => ({ ...o, segmentIndex: 0 })),
  };
}

/**
 * Resolve a free-standing segment to its virtual wall, with the cook side
 * resolved the SAME way materializeRunObjects does (`storedRunLegSides` — the
 * run's one side, `runSide.js`) so an appliance's centerParam — measured along this wall —
 * matches where the materialize will place it. Returns { wall, lineLength,
 * cookSide, sided } or null.
 */
export function resolveFreeSegment(storedRun, freeSegIndex, room) {
  const seg = storedRun.segments?.[freeSegIndex];
  if (!seg?.freestanding) return null;
  const freeSegs = (storedRun.segments || []).filter((s) => s.freestanding);
  const otherRuns = groupOtherCabinetRuns(room, storedRun.id);
  const defaults = defaultSidedness(seg, freeSegs, otherRuns);
  // The run's ONE side, carried round its bends — the answer the fill uses.
  const cookSide = storedRunLegSides(storedRun, room).get(freeSegIndex);
  const wall = synthesizeVirtualWall(seg.start, seg.end, {
    side: cookSide,
    id: `freestanding_${freeSegIndex}`,
  });
  const lineLength = Math.hypot(
    seg.end.x - seg.start.x,
    seg.end.z - seg.start.z
  );
  return { wall, lineLength, cookSide, sided: seg.sided ?? defaults.sided };
}

/**
 * The datum a free-standing leg's numbers are measured from, for an object
 * that leg emitted: its two endpoints in world space, the inward normal
 * (pointing to the cook side), and the leg's own `depth`.
 *
 * Read off the virtual wall `resolveFreeSegment` builds rather than off
 * `segment.start` / `segment.end` directly, so the line drawn on the plan is
 * the same one the fill measures against — including its orientation, which
 * flips with the cook side.
 *
 * The line is NOT the island's centreline: `dividerLine` puts it at the front
 * row's back face — the plane between the rows on a double-sided island, and
 * on a single-row one the plane its finished back and any knee sit BEHIND (on
 * Side B). Nothing in the panel makes sense without seeing where it actually
 * is.
 *
 * `previewRun` is the EDITED stored run of a live drag (`previewForRunEdit`'s
 * `stored`). Nothing writes to the room store before mouseup, so without it
 * the datum — and with it the Side A / Side B labels and the Move disc — is
 * drawn at the pre-drag position while the island's ghost rides the cursor.
 * The run-end handles already read the previewed run for the same reason.
 *
 * @returns {{start, end, inward:{x,z}, cookSide, sided, depth}|null}
 */
export function freeSegmentDatum(room, object, previewRun = null) {
  if (!room || !object?.runId) return null;
  const current = findRunByIdOrAlias(room.cabinetRuns, object.runId);
  if (!current) return null;
  // Matched on the RESOLVED id, so a run carrying an alias still swaps.
  const stored = previewRun?.id === current.id ? previewRun : current;
  // Resolved against the run — an untagged island object still has a leg.
  const segIndex = freeSegIndexOf(object, stored);
  if (segIndex == null) return null;
  const resolved = resolveFreeSegment(stored, segIndex, room);
  const a = resolved?.wall?.alignment?.bottomLeft;
  const b = resolved?.wall?.alignment?.bottomRight;
  if (!a || !b) return null;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  if (!(len > 0)) return null;
  return {
    start: { x: a.x, z: a.z },
    end: { x: b.x, z: b.z },
    // Where the leg's MOVE handle sits, and the anchor the Side A / Side B
    // labels are offset from.
    mid: { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 },
    // `synthesizeVirtualWall` orients the wall so its inward normal points at
    // the cook side; the same expression `getWallAxes` uses.
    inward: { x: -dz / len, z: dx / len },
    cookSide: resolved.cookSide,
    sided: resolved.sided,
    depth: stored.segments[segIndex]?.depth ?? null,
  };
}

/**
 * Nearest free-standing leg (island/peninsula) to a world point, by distance
 * from the leg's drawn line. Returns { d, run, freeSegIndex } or null.
 */
export function nearestFreeSegment(world, room) {
  let best = null;
  for (const run of room.cabinetRuns || []) {
    const segs = run.segments || [];
    for (let i = 0; i < segs.length; i += 1) {
      const s = segs[i];
      if (!s.freestanding) continue;
      const d = pointToSegmentDistance(world, s.start, s.end);
      if (!best || d < best.d) best = { d, run, freeSegIndex: i };
    }
  }
  return best;
}

/**
 * Nearest run wall to a world point, over EVERY run's wall-backed segments —
 * manual and derived resolve alike now (spec §8). A stale derived run is not
 * a target: listed first on a wall it shares with the live run, it would win
 * the tie the strict `<` below never breaks.
 * @returns {{ d, run, segmentIndex, wall }|null}
 */
export function nearestRunSegment(world, room) {
  const resolver = buildSegmentWallResolver(room);
  let best = null;
  for (const run of room?.cabinetRuns || []) {
    if (isStaleDerivedRun(run, room.objects)) continue;
    const segs = run.segments || [];
    for (let i = 0; i < segs.length; i += 1) {
      const s = segs[i];
      if (s.freestanding) continue;
      const wall = resolver.resolve(s);
      if (!wall?.alignment) continue;
      const d = pointToSegmentDistance(
        world,
        wall.alignment.bottomLeft,
        wall.alignment.bottomRight
      );
      if (!best || d < best.d) best = { d, run, segmentIndex: i, wall };
    }
  }
  return best;
}

/**
 * Clamp an appliance centre on a free-standing leg: project `world` onto the
 * leg's virtual wall to a param, then slide it clear of the leg's other
 * appliances and ends via the engine clamp. Returns the param or null.
 */
export function clampFreestandingApplianceCenter(
  storedRun,
  freeSegIndex,
  type,
  world,
  room
) {
  const resolved = resolveFreeSegment(storedRun, freeSegIndex, room);
  if (!resolved) return null;
  const { wall, lineLength } = resolved;
  const rawParam = worldToWallParam(wall, world);
  if (rawParam == null) return null;
  // Single-segment run on the virtual wall, carrying this leg's BAYS
  // (reindexed to segment 0) so the clamp keeps clear of them. Reading the
  // legacy `appliances[]` here saw nothing on a bays-shaped run, so a second
  // island appliance could be dropped atop the first (F4).
  const segBays = baysForRun(storedRun)
    .filter((b) => (b.segmentIndex ?? 0) === freeSegIndex)
    .map((b) => ({ ...b, segmentIndex: 0 }));
  const run = {
    id: storedRun.id,
    segments: [{ wall, startParam: 0, endParam: lineLength }],
    bays: segBays,
    bayOverrides: [],
  };
  return clampApplianceCenter(run, 0, type, rawParam, {
    floorY: getFloorY(room),
    openings: [],
  });
}

/**
 * Resolve the appliance-drop target under `world`: the nearest wall-backed
 * run-segment OR free-standing leg, whichever is closer, with the clamped
 * centerParam. Returns { stored, segmentIndex, centerParam } or null when the
 * cursor isn't over a run or the appliance can't fit there.
 */
export function resolveApplianceDropTarget(
  world,
  room,
  openings,
  applianceType
) {
  // Wall-backed candidate: nearest run wall, manual or derived, in the room
  // frame — one finder for every run.
  let wallCand = null;
  const wallSnap = nearestRunSegment(world, room);
  if (wallSnap && wallSnap.d <= SNAP_TOLERANCE) {
    wallCand = {
      d: wallSnap.d,
      stored: wallSnap.run,
      segmentIndex: wallSnap.segmentIndex,
      kind: 'wall',
      wall: wallSnap.wall,
    };
  }
  // Free-standing candidate: nearest island/peninsula leg.
  let freeCand = null;
  const freeSnap = nearestFreeSegment(world, room);
  if (freeSnap && freeSnap.d <= SNAP_TOLERANCE) {
    freeCand = {
      d: freeSnap.d,
      stored: freeSnap.run,
      segmentIndex: freeSnap.freeSegIndex,
      kind: 'free',
    };
  }
  const cand = [wallCand, freeCand]
    .filter(Boolean)
    .sort((a, b) => a.d - b.d)[0];
  if (!cand) return null;

  // centerParam null => a run is under the cursor but the appliance can't fit
  // (lets the caller distinguish "no run here" from "no room here").
  let centerParam = null;
  if (cand.kind === 'wall') {
    // Project the cursor onto the run's own resolved room wall (already in hand
    // from the snap), then slide it clear via the engine clamp.
    const rawCenter = worldToWallParam(cand.wall, world);
    if (rawCenter != null) {
      centerParam = clampApplianceCenterParam(
        cand.stored,
        cand.segmentIndex,
        applianceType,
        rawCenter,
        room,
        openings
      );
    }
  } else {
    centerParam = clampFreestandingApplianceCenter(
      cand.stored,
      cand.segmentIndex,
      applianceType,
      world,
      room
    );
  }
  return { stored: cand.stored, segmentIndex: cand.segmentIndex, centerParam };
}

/**
 * Segment index for an anchor param: the segment on `wallId` whose param range
 * contains it (robust to a duplicate same-wall segment). Falls back to the
 * first segment on the wall.
 */
function resolveSegmentIndex(storedRun, wallId, anchorParam) {
  const segs = storedRun.segments || [];
  for (let i = 0; i < segs.length; i += 1) {
    if (segs[i].wallId !== wallId) continue;
    const lo = Math.min(segs[i].startParam, segs[i].endParam);
    const hi = Math.max(segs[i].startParam, segs[i].endParam);
    if (anchorParam >= lo - PARAM_EPS && anchorParam <= hi + PARAM_EPS)
      return i;
  }
  return segs.findIndex((s) => s.wallId === wallId);
}

/**
 * Drop no-op overrides from `next`, re-solve the run, and package the result.
 * Shared tail of applyBayOverride (bay-anchored) and applyCornerOverride
 * (corner-anchored): both build the new override list, then finish identically.
 */
function rematerializeWithOverrides(stored, room, openings, next, overrideId) {
  // Drop overrides that no longer do anything (no width, no real type, no
  // appearance) — a corner override survives while it carries appearance or
  // the recorded corner treatment a derived run's re-solve honours.
  const cleaned = next.filter(
    (o) =>
      o.width != null ||
      // A bridge cabinet's own height (an upper bay — `upperBayOverrides`).
      o.height != null ||
      // An upper's glass doors, or its opt-out of the run's (`glassUppers`).
      o.glass != null ||
      // An upper's opt-out of the run's stacked row (`upperStack.js`).
      o.noStack === true ||
      // A base's roll-out trays (`rollOuts.js`) — often the record's only
      // setting.
      o.rollOuts != null ||
      // Drawers beside a sink bay's basin (`sinkUnits.js`).
      o.sinkDrawers != null ||
      // A glass holder under an upper (`glassHolders.js`).
      o.glassHolder != null ||
      (o.type != null && o.type !== 'standard') ||
      o.appearance != null ||
      o.treatment != null ||
      // The user's pick at a LOWER corner (`cornerChoice.userChoiceField`).
      o.userTreatment != null
  );
  // Flag an auto run as edited BEFORE the re-solve so its objects are stamped
  // userEdited (and so scoped regeneration preserves it — regen-merge policy).
  const storedForSolve = { ...stored, bayOverrides: cleaned, pins: undefined };
  // Marks generated output as edited (regen-merge provenance) AND promotes a
  // scan run to "the user's" — see `regeneratesOnWallMove`.
  if (isDerivedRun(stored)) storedForSolve.userEdited = true;
  const run = rehydrateRun(storedForSolve, room);
  const resolve = materializeRunObjects(run, room, openings);
  const storedRun = stampStoredRun(
    storedRunFromRun({ ...stored, bayOverrides: cleaned }),
    room
  );
  delete storedRun.pins; // migrated to bayOverrides
  if (isDerivedRun(stored)) storedRun.userEdited = true;
  return { objects: resolve.objects, storedRun, overrideId, resolve };
}

/**
 * Upsert the override for a selected bay and re-materialize its run.
 *
 * `patch` is `{ width }` (fixed-width, non-position-locking) or `{ type }`
 * (appearance, non-locking). If the bay already carries an `overrideId` its
 * override is updated in place (so a slid fixed bay still edits the right
 * override); otherwise a new override is anchored at the bay's current centre.
 * Overrides that no longer do anything (no width, no/standard type) are dropped.
 *
 * A box on a FREE-STANDING leg anchors in the leg's own frame. Its
 * `parentWallIds` names the wall the fill SYNTHESIZED for that leg, which
 * exists only for the duration of a solve — the room holds no such wall, so
 * the wall-param lookup returned null and every island bay edit was refused
 * ("Could not update this bay"). The leg's line is the frame instead
 * (`resolveFreeSegment`, the same one the along-run slide is drawn from), and
 * the segment index comes from the object's own `freeSegIndex`, which is
 * already a STORED index — `fillFreestandingSegments` filters the run's
 * overrides by it and has always honoured what it finds. Read side first,
 * write side second: the panel's gate on this section came down with it.
 *
 * Returns { objects, storedRun, overrideId } or null.
 */
export function applyBayOverride(room, runId, bay, patch, openings = []) {
  const found = (room.cabinetRuns || []).find((r) => r.id === runId);
  if (!found) return null;
  // A patch may move the run's uppers in the same write (`upperSpec`, merged
  // as `applyRunUpperSpec` merges it): the one click that hangs them where a
  // wall cabinet with drawers fits (`catalog.hangFixFor`) sets the bay's
  // front with it, so neither lands without the other.
  const { upperSpec, ...bayPatch } = patch || {};
  const stored = upperSpec ? withUpperSpecPatch(room, found, upperSpec) : found;
  if (!stored) return null;
  const built = bayOverridesWith(room, stored, bay, bayPatch);
  if (!built) return null;
  return rematerializeWithOverrides(
    stored,
    room,
    openings,
    built.overrides,
    built.overrideId
  );
}

/**
 * The override record that currently governs this bay, or null.
 *
 * `bayOverrideIdOf` says which id a bay answers to; this says what is stored
 * under it. The bay EDGE drag asks so it can stand down on a TALL span, whose
 * one record is emitted as several modules.
 */
export function governingOverrideOf(stored, bay) {
  const id = bayOverrideIdOf(bay);
  if (!id) return null;
  return overridesForRun(stored).find((o) => o.id === id) || null;
}

/**
 * The id of the override that governs this bay — the one it already carries,
 * or the one a first edit on it will mint.
 *
 * Deterministic on purpose, and published so nothing re-derives it: the drag
 * finds its bay in the PREVIEW's re-solved objects by this id, because a
 * re-solve re-mints object ids from their params and the box under the cursor
 * is a different object by the time the ghost is drawn.
 */
export function bayOverrideIdOf(bay) {
  return bay?.overrideId ?? (bay?.id ? `ov_${bay.id}` : null);
}

/**
 * WHERE a bay sits on its run: the segment it belongs to and its anchor param
 * along that segment's line.
 *
 * Published because the bay EDGE drag must resolve the same segment the WRITE
 * will (`bayOverridesWith` calls it to seat a new override, and the drag calls
 * it to find the wall the bay's width is measured along). Two answers to "which
 * segment is this box on" is two chances to measure a width against one line
 * and write it against another.
 *
 * Returns `{ segmentIndex, anchorParam, wall }` or null. A free-standing leg
 * has no room wall, so `wall` is the synthesized line the fill packs against —
 * the same one `runSegmentWall` returns.
 */
export function bayAnchorOnRun(room, stored, bay) {
  if (!room || !stored || !bay?.center) return null;
  const ownedByRun = !!bay?.runId && runAnswersTo(stored, bay.runId);
  const freeSegIndex = ownedByRun ? freeSegIndexOf(bay, stored) : null;
  const wallId = freeSegIndex == null ? bayWallId(bay) : null;
  if (freeSegIndex == null && !wallId) return null;
  const point = { x: bay.center.x, z: bay.center.z };
  const freeWall =
    freeSegIndex == null
      ? null
      : resolveFreeSegment(stored, freeSegIndex, room)?.wall;
  const anchorParam =
    freeSegIndex == null
      ? runWallParamForPoint(stored, wallId, point, room)
      : freeWall
        ? worldToWallParam(freeWall, point)
        : null;
  if (anchorParam == null) return null;
  const segmentIndex =
    freeSegIndex == null
      ? resolveSegmentIndex(stored, wallId, anchorParam)
      : freeSegIndex;
  if (segmentIndex < 0) return null;
  return {
    segmentIndex,
    anchorParam,
    wall: freeWall || runSegmentWall(room, stored, segmentIndex),
  };
}

/**
 * ONE kind of pin (user ruling 2026-10-26): a cabinet whose width the user
 * set keeps that width AND its spot, while the spot exists — the fill still
 * falls back to the width alone where it does not (`overridesForZone`, the
 * upper tier's `holdsPlaceIn`). There used to be a second kind, width only,
 * which the packer slid along to keep the run tight: a neighbour dragged over
 * its middle took its spot, and its width was lost with nothing left to wear
 * it.
 *
 * Where a width typed in the panel puts the box: the edge on the run's START
 * side stays and the other moves, as an edge drag keeps the edge you did not
 * grab. `width` is the new width; the box is read where it stands now.
 */
function pinnedWidthAnchor(room, stored, bay, width) {
  const seated = bayAnchorOnRun(room, stored, bay);
  const was = bay?.dimensions?.x;
  if (!seated || !(was > 0) || !(width > 0)) return null;
  const seg = (stored.segments || [])[seated.segmentIndex];
  // A reversed segment (`endParam` below `startParam`) starts at its HIGH
  // param; a free leg's frame runs 0..length from its start.
  const startHigh =
    !!seg &&
    Number.isFinite(seg.startParam) &&
    Number.isFinite(seg.endParam) &&
    seg.endParam < seg.startParam;
  const anchorParam = startHigh
    ? seated.anchorParam + was / 2 - width / 2
    : seated.anchorParam - was / 2 + width / 2;
  return { anchorParam, segmentIndex: seated.segmentIndex };
}

/** The last answer per stored run, so a drag keeps one stored identity. */
const pinsMemo = new WeakMap();

/**
 * The stored run with every WIDTH-ONLY pin it still carries placed where its
 * box stands now — the same run (same object) when there is none. Saved runs
 * predate the one kind of pin; reading them through this makes their pins
 * stops for a drag at once, and the next edit of the run saves them placed.
 * Nothing moves: each takes the spot it is already standing in. A record no
 * box wears is left as it is, and a tall span is a different edit.
 */
export function withPinsPlaced(room, stored) {
  if (!room || !stored) return stored;
  const hit = pinsMemo.get(stored);
  if (hit && hit.room === room) return hit.out;
  let changed = false;
  const overrides = overridesForRun(stored).map((o) => {
    if (!(o?.width > 0) || o.positioned === true || isTallOverride(o)) return o;
    const box = (room.objects || []).find(
      (b) =>
        b?.overrideId === o.id && !!b.runId && runAnswersTo(stored, b.runId)
    );
    const seated = box ? bayAnchorOnRun(room, stored, box) : null;
    if (!seated) return o;
    changed = true;
    return {
      ...o,
      anchorParam: seated.anchorParam,
      segmentIndex: seated.segmentIndex,
      positioned: true,
    };
  });
  const out = changed ? { ...stored, bayOverrides: overrides } : stored;
  pinsMemo.set(stored, { room, out });
  return out;
}

/**
 * The OVERRIDE LIST a bay edit produces, without re-solving anything.
 *
 * Split out of `applyBayOverride` so the bay-edge DRAG can preview a width
 * without committing one: the drag needs the edited stored run on every
 * pointer frame (`previewForRunEdit` re-solves it into a ghost), and the panel
 * needs the same list once, on ✓. Two copies of this would be two chances to
 * write a different override from the one the ghost showed.
 *
 * Pure: takes the room only to resolve an anchor, returns
 * `{ overrides, overrideId }` or null. Every refusal below is a WRITE
 * BOUNDARY — the engine never substitutes a value for a bad one.
 */
export function bayOverridesWith(room, storedRaw, bay, patch) {
  // Any width-only pin the run still carries is written placed with this
  // edit (`withPinsPlaced`) — the one kind of pin.
  const stored = withPinsPlaced(room, storedRaw);
  // `freeSegIndexOf` answers from the RUN when an object carries no stamp, so
  // it would hand a single-leg run's index to ANY object — the ownership test
  // is what keeps this path to boxes the run actually emitted. A wall-backed
  // box was refused by its missing `parentWallIds` before; a leg has no such
  // accident to rely on.
  const ownedByRun = !!bay?.runId && runAnswersTo(stored, bay.runId);
  const freeSegIndex = ownedByRun ? freeSegIndexOf(bay, stored) : null;
  const wallId = freeSegIndex == null ? bayWallId(bay) : null;
  if (freeSegIndex == null && !wallId) return null;

  // The write boundary: a width the packer reads must be a real length (a NaN
  // or 0 would persist an override that packs to nothing — spec §5, a span
  // with no incumbent carries its footprint), and a tall span narrower than
  // one module has no module to emit. Refused here, toasted by the hook;
  // the engine never substitutes a value.
  if (
    patch.width != null &&
    !(Number.isFinite(patch.width) && patch.width > 0)
  ) {
    return null;
  }
  // …and the same for a bridge cabinet's height, which only an UPPER has: a
  // base cabinet's height is the counter's, and the lower fill never reads the
  // field — writing it there would store a setting that does nothing.
  if (patch.height != null) {
    if (!(Number.isFinite(patch.height) && patch.height > 0)) return null;
    if (bayTier(bay) !== 'upper') return null;
  }
  // …and an upper's height is one its line makes at this box's width, under
  // the run's height and the ceiling (`upperHeights.bayHeightFor`, the rule
  // the panel greys its chips by and the fill builds by). One it cannot build
  // is refused; one at or above the run's IS the run's height, and is stored
  // as nothing.
  const written =
    patch.height != null ? upperHeightWrite(room, stored, bay, patch) : patch;
  if (!written) return null;
  // …and glass doors are an upper's, a boolean, and only where its line makes
  // a glass door for the box (`glassUppers.glassUnavailableReason`, the rule
  // the panel greys its toggle by and the fill builds by).
  if (patch.glass != null && !upperGlassWritable(room, stored, bay, patch))
    return null;
  // …and roll-out trays are a base's, 1..3 or cleared, and only where the
  // box can take them (`rollOuts.rollOutUnavailableReason`, the rule the
  // panel greys its chips by and the fill builds by).
  if (patch.rollOuts !== undefined && !rollOutsWritable(stored, bay, patch))
    return null;
  // …and drawers beside a basin are a sink bay's, a side or cleared, and
  // only a side its line makes a sink cabinet with at this width
  // (`sinkUnits.sinkUnitChoices`, the rule the panel's Sink cabinet row
  // offers and the fill builds by).
  if (
    patch.sinkDrawers !== undefined &&
    !sinkDrawersWritable(stored, bay, patch)
  )
    return null;
  // …and a valance bay's style and the sink it is kept over are an upper's:
  // one of the two styles or cleared, a bay id (`valanceBays.js`).
  if (
    patch.valanceStyle !== undefined &&
    (bayTier(bay) !== 'upper' ||
      !(
        patch.valanceStyle === null ||
        VALANCE_STYLES.includes(patch.valanceStyle)
      ))
  )
    return null;
  if (
    patch.overSinkBayId !== undefined &&
    (bayTier(bay) !== 'upper' || typeof patch.overSinkBayId !== 'string')
  )
    return null;
  // …and a glass holder is an upper's, `true` or cleared, and only under a
  // cabinet one fits (`glassHolders.glassHolderReason`, the rule the panel
  // greys its toggle by and the fill stamps by).
  if (
    patch.glassHolder !== undefined &&
    (bayTier(bay) !== 'upper' ||
      !(
        patch.glassHolder === null ||
        (patch.glassHolder === true && !glassHolderReason(bay))
      ))
  )
    return null;
  // …and where a rack's short cabinet stands is an upper's: above, below,
  // none or cleared (above, `rackStacks.js`).
  if (
    patch.rackCabinet !== undefined &&
    (bayTier(bay) !== 'upper' ||
      !(
        patch.rackCabinet === null ||
        RACK_CABINET_POSITIONS.includes(patch.rackCabinet)
      ))
  )
    return null;
  // …and that cabinet's front is an upper's: glass doors, an open shelf, or
  // cleared (doors).
  if (
    patch.rackCabinetFront !== undefined &&
    (bayTier(bay) !== 'upper' ||
      !(
        patch.rackCabinetFront === null ||
        (patch.rackCabinetFront !== 'doors' &&
          RACK_CABINET_FRONTS.includes(patch.rackCabinetFront))
      ))
  )
    return null;
  // …and a stacked row's opt-out is an upper's, `true` or cleared (C18).
  if (
    patch.noStack !== undefined &&
    (bayTier(bay) !== 'upper' ||
      !(patch.noStack === true || patch.noStack === null))
  )
    return null;

  const overrides = overridesForRun(stored);
  const existingIdx =
    bay.overrideId != null
      ? overrides.findIndex((o) => o.id === bay.overrideId)
      : -1;

  let next;
  let overrideId;
  if (existingIdx >= 0) {
    overrideId = overrides[existingIdx].id;
    // A WIDTH-LESS record lives with the box that wore it (`overrideHomes`):
    // its stored point is only its first seat, and after a re-pack the box
    // wearing it may stand elsewhere. The box making the edit owns the record,
    // so it is re-seated there — or a width typed on it packs the new fixed
    // box at the old point, one slot over. A record with a width is its own
    // box and keeps its anchor (a slid fixed bay's centre is not its anchor).
    const reseat =
      overrides[existingIdx].width == null
        ? bayAnchorOnRun(room, stored, bay)
        : null;
    // Re-stamp the tier: the box making the edit owns the record, which heals
    // an override written before tiers existed.
    next = overrides.map((o, i) =>
      i === existingIdx
        ? {
            ...o,
            ...(reseat
              ? {
                  anchorParam: reseat.anchorParam,
                  segmentIndex: reseat.segmentIndex,
                }
              : {}),
            tier: bayTier(bay),
            ...written,
          }
        : o
    );
  } else {
    const seated = bayAnchorOnRun(room, stored, bay);
    if (!seated) return null;
    const { anchorParam, segmentIndex } = seated;
    overrideId = bayOverrideIdOf(bay);
    // Drop any existing override with this id before adding (a bay that didn't
    // carry its overrideId — e.g. a pre-fix sink base — could otherwise stack
    // duplicates, and a stale one would keep winning). Self-heals such data.
    next = [
      ...overrides.filter((o) => o.id !== overrideId),
      {
        id: overrideId,
        segmentIndex,
        anchorParam,
        tier: bayTier(bay),
        ...written,
      },
    ];
  }

  // A valance picked over a sink is kept over it (`valanceBays.js`): an
  // upper saved before the fill stamped `overSinkBayId` says nothing, so the
  // run's own sink bay under the record is named here.
  const asWritten = next.find((o) => o.id === overrideId);
  if (asWritten?.type === 'valance' && !asWritten.overSinkBayId) {
    const sinkBayId = sinkBayUnder(
      stored,
      asWritten.segmentIndex ?? 0,
      asWritten.anchorParam
    );
    if (sinkBayId)
      next = next.map((o) =>
        o.id === overrideId ? { ...o, overSinkBayId: sinkBayId } : o
      );
  }

  const record = next.find((o) => o.id === overrideId);
  if (
    isTallOverride(record) &&
    record.width != null &&
    record.width < narrowestModuleIn(stored) * INCHES_TO_METERS
  ) {
    return null;
  }

  // A fixed width is a BOX, and a family that sells a DISCRETE set has to
  // sell it: snapped here rather than only in the panel, because `pushFixed`
  // pushes an override's width into the run VERBATIM — this is the boundary,
  // and any other caller (a tool, a test, a future surface) would otherwise
  // plant a width the family cannot build. On a line with a size catalog
  // (Venus) the set is the widths it makes THIS box's kind at, with the front
  // being written (`snapBayWidthIn` — the one rule the drag and the panel
  // ask): a 39" drawer base becomes the 36" Venus makes. A no-op for a
  // continuous family, which is what keeps a deliberately typed 15.8" from
  // coming back 15" on Generic Custom. A tall span is exempt either way: it
  // packs as whole modules plus a filler.
  if (record && !isTallOverride(record) && record.width != null) {
    const snapped =
      snapBayWidthIn(stored, bay, record.width / INCHES_TO_METERS, {
        front: record.type,
      }) * INCHES_TO_METERS;
    if (Math.abs(snapped - record.width) > 1e-9) {
      next = next.map((o) =>
        o.id === overrideId ? { ...o, width: snapped } : o
      );
    }
  }

  // A width set WITHOUT a place — typed in the panel — holds its place too:
  // one kind of pin. A drag says where itself (`anchorParam` + `positioned`).
  // A width cleared (Auto, a click on the pin) lets the box go entirely.
  const settled = next.find((o) => o.id === overrideId);
  if (
    patch.width > 0 &&
    patch.anchorParam == null &&
    patch.positioned == null &&
    settled &&
    !isTallOverride(settled)
  ) {
    const placed = pinnedWidthAnchor(room, stored, bay, settled.width);
    if (placed) {
      next = next.map((o) =>
        o.id === overrideId ? { ...o, ...placed, positioned: true } : o
      );
    }
  } else if (patch.width === null && settled?.positioned != null) {
    next = next.map((o) => {
      if (o.id !== overrideId) return o;
      const { positioned: _released, ...rest } = o;
      return rest;
    });
  }

  return { overrides: next, overrideId };
}

/**
 * The patch an upper HEIGHT edit writes: as given when the line makes that
 * height at this box's width; `height: null` when it is at or above the
 * run's (it IS the run's height, which needs no setting); null — refused —
 * when the line cannot build it or the ceiling has no room for it.
 */
function upperHeightWrite(room, stored, bay, patch) {
  const { heightM, reason } = bayHeightFor(
    stored,
    bay,
    runUpperBand(stored, room),
    patch.height
  );
  if (reason) return null;
  return heightM == null ? { ...patch, height: null } : patch;
}

/**
 * May `patch.glass` be written on `bay`? A boolean, on an upper that is not a
 * filler or an open shelf; `true` only where the line makes a glass door for
 * the box at the height the run builds it, in the look the run gives it —
 * what the fill asks before it draws one (`upperTier.glassFor`).
 */
/**
 * May `patch.rollOuts` be written on `bay`: cleared (`null`) always on a base,
 * a count only when it is 1..3, the box as it stands can take trays in its
 * run's line, and its front has room for that many (`rollOutRoomFor` — what
 * the panel greys its larger chips by). A count is therefore refused on a
 * drawer base, yet survives in a record whose front is switched to drawers
 * later: the fill then draws none, and switching back brings them back.
 */
function rollOutsWritable(stored, bay, patch) {
  if (bayTier(bay) !== 'lower') return false;
  if (patch.rollOuts === null) return true;
  const n = normalizeRollOuts(patch.rollOuts);
  if (n == null) return false;
  return (
    rollOutUnavailableReason(familyOf(stored), bay) == null &&
    n <= rollOutRoomFor(bay)
  );
}

/**
 * May `patch.sinkDrawers` be written on `bay`: cleared (`null`) always on a
 * base; a side only on a sink box whose line makes a sink cabinet with
 * drawers on that side at the box's width.
 */
function sinkDrawersWritable(stored, bay, patch) {
  if (bayTier(bay) !== 'lower') return false;
  if (patch.sinkDrawers === null) return true;
  const side = normalizeSinkDrawers(patch.sinkDrawers);
  if (!side) return false;
  return sinkUnitChoices(familyOf(stored), bay, {
    profileId: profileOf(stored).id,
  }).some((c) => c.id === side);
}

function upperGlassWritable(room, stored, bay, patch) {
  if (typeof patch.glass !== 'boolean') return false;
  if (bayTier(bay) !== 'upper') return false;
  if (
    bay.category?.filler ||
    bay.category?.openShelf ||
    bay.category?.floatingShelves
  )
    return false;
  if (patch.glass === false) return true;
  const record = governingOverrideOf(stored, bay);
  const look =
    effectiveAppearance({
      override: record,
      run: stored,
      ctx: { roomAppearance: room?.cabinetAppearance },
    }) || {};
  const band = runUpperBand(stored, room);
  const runIn = runUpperHeightIn(stored, bay, band);
  if (runIn == null) return false;
  const atRunHeight = {
    ...bay,
    dimensions: { ...bay.dimensions, y: runIn * INCHES_TO_METERS },
  };
  return !glassUnavailableReason(stored, atRunHeight, look);
}

/**
 * Hand a SCANNED tall unit from the scan to the RUN — #1843's
 * "Let the run size it".
 *
 * A scanned tall unit reaches the fill as a **hard piece**: `scanApplianceAnchors`
 * reserves it as a `tallUnit` bay, the packer packs AROUND that span, and
 * `emitTallUnits` re-seats the scanned object into it verbatim. Nothing is
 * built there, which is why the panel can offer nothing:
 *
 *   - a WIDTH override anchored inside a hard piece reaches no free sub-span,
 *     so it would be silently ignored;
 *   - a TYPE override is read by `applyBayType`, which only runs inside
 *     `emitBay` — on boxes the fill builds. There is no box, so the override
 *     would be inert, which is worse than a disabled control.
 *
 * This is the escape hatch, and it is a change of OWNERSHIP rather than of
 * geometry: drop the bay, write a width-less tall override at the same anchor,
 * re-solve. The auto bay containing that anchor then goes tall and keeps
 * reflowing (the degenerate one-bay case of the tall span), so both the width
 * editor and the type picker become live — and `runEndRange` no longer clamps
 * to a hard bay, which is what unpins a run end a tall unit sits flush against.
 *
 * `patch` rides on the override, so picking a non-tall FRONT promotes and
 * retypes in one write: choosing "Doors" on a scanned tall unit means "stop
 * treating this as measured", which is the same three steps with a different
 * `type`.
 *
 * The scanned unit's APPEARANCE carries onto the span when it has one — the
 * incumbent-reuse rule — and onto every box the span emits, since a span's
 * boxes are content-keyed and there is no way to say "this one was the
 * scanned one". What is NOT preserved is the object: the measured body is
 * swept and the engine's modules stand in its place. That is the trade the
 * action makes, and it is the whole point of it.
 *
 * @returns {{ objects, storedRun, overrideId, resolve }|null} null when the
 *   object is not a scanned tall unit — the caller says so rather than
 *   silently writing an override that changes nothing.
 */
export function promoteScannedTallUnit(
  room,
  runId,
  object,
  openings = [],
  patch = {}
) {
  const stored = (room?.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored) return null;
  const hit = runBayForObject(room, object);
  const bay = hit?.bay;
  // The SAME predicate the panel disables its controls with, so the button can
  // never be offered for a bay this refuses (and vice versa).
  if (!bay || !isReseatedTallUnit(bay, object)) return null;
  if (!Number.isFinite(bay.anchorParam)) return null;

  const overrideId = `ov_${bay.id}`;
  const appearance = object?.appearance ?? null;
  const next = [
    ...overridesForRun(stored).filter((o) => o.id !== overrideId),
    {
      id: overrideId,
      segmentIndex: bay.segmentIndex ?? 0,
      anchorParam: bay.anchorParam,
      tier: 'lower',
      type: 'tall',
      // NO width, deliberately: that is what makes the auto bay under this
      // anchor go tall and keep reflowing, instead of pinning a second hard
      // piece where the first one was.
      ...(appearance ? { appearance } : {}),
      ...patch,
    },
  ];

  const promoted = {
    ...stored,
    bays: baysForRun(stored).filter((b) => b.id !== bay.id),
    // The run is expressed in BAYS from here on; a stale legacy `appliances`
    // list would shadow the drop on the next read (same reason
    // `editBayAnchor` clears it).
    appliances: undefined,
  };
  // NOTE the merge's `sweptTallBodyWarnings` tripwire fires on this by design
  // — it watches for a measured tall body nothing re-emits, which is exactly
  // what a promotion produces on purpose. It is console-only
  // (`reportMergeDiagnostics`), so this is developer noise rather than a
  // user-visible problem, but it does mean the tripwire cries wolf once per
  // promotion.
  return rematerializeWithOverrides(promoted, room, openings, next, overrideId);
}

/** The id a NEW corner record takes. The lower keeps the original `ovc_<i>`
 *  so every override persisted before tiers keeps resolving to its box. */
const cornerOverrideId = (tier, cornerIndex) =>
  tier === 'upper' ? `ovc_upper_${cornerIndex}` : `ovc_${cornerIndex}`;

/**
 * Upsert the per-corner appearance override for a corner cabinet `bay` (keyed by
 * its `cornerIndex`) and re-materialize its run. A corner unit isn't an auto bay
 * — it can't be anchored by wall param — so its Unit-scope appearance rides on a
 * { id, cornerIndex, appearance } override that fillRun reads via cornerOverride.
 *
 * Returns { objects, storedRun, overrideId } or null.
 */
export function applyCornerOverride(room, runId, bay, patch, openings = []) {
  const stored = (room.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored) return null;
  let cornerIndex = bay?.cornerIndex;
  if (cornerIndex == null) {
    // A corner materialized before cornerIndex tagging (saved room.objects, not
    // re-solved on load) won't carry it; recover it from geometry so the first
    // Unit-scope edit still lands on the right corner.
    cornerIndex = cornerIndexForUnit(rehydrateRun(stored, room), bay);
  }
  if (cornerIndex == null || cornerIndex < 0) return null;

  const overrides = overridesForRun(stored);
  // A corner UPPER carries the corner LOWER's `cornerIndex`, so the tier has to
  // be part of both the lookup and the id (`cornerOverrideId`) — otherwise
  // editing one hijacks the other's record.
  const tier = bayTier(bay);
  const existingIdx = overrides.findIndex(
    (o) => o.cornerIndex === cornerIndex && overrideTier(o) === tier
  );
  const overrideId =
    existingIdx >= 0
      ? overrides[existingIdx].id
      : cornerOverrideId(tier, cornerIndex);

  let next;
  if (existingIdx >= 0) {
    next = overrides.map((o, i) =>
      i === existingIdx ? { ...o, tier, ...patch } : o
    );
  } else {
    // Drop any stale override with this id first so re-edits don't stack.
    next = [
      ...overrides.filter((o) => o.id !== overrideId),
      { id: overrideId, cornerIndex, tier, ...patch },
    ];
  }

  return rematerializeWithOverrides(stored, room, openings, next, overrideId);
}

const CABINET_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
  // The full cabinetry surface family (browser test 2 follow-up,
  // 2026-08-31): panels and appliance surrounds take the same finishes, and
  // the narrow list left them behind on restyles.
  'corner-fill-cabinet',
  'blank-panel',
  'fridge-surround',
  'laundry-surround',
  'oven-surround',
]);
const isCabinetObject = (o) => CABINET_TYPES.has(o?.type);

/**
 * THE membership rule for a run's cabinetry - shared by the appearance
 * WRITER (applyAppearanceAtScope) and the scope-preview HIGHLIGHT
 * (cabinetScopeIdsByRoom), because a preview that disagrees with the write
 * is a lie about what Apply will do (browser follow-ups, 2026-08-31: the
 * fridge surround restyled but never highlighted). Membership is, in
 * order: owned (runId, or one of the run's aliases), bay-stamped (bayId - drop-added installs strip
 * runId on purpose), or wall-referenced (parentWallIds / cabinetWallRefs
 * - generated installs carry neither id).
 *
 * The wall fallback NEVER claims a vanity assembly's cabinet. A vanity is
 * also a `lower-cabinet` with `parentWallIds`, so before this gate a run
 * drawn on a bathroom wall swept the unrelated vanity beside it into the
 * run's appearance scope and selection highlight. A run-OWNED vanity
 * cabinet (phase 3, tagged `runId`) is untouched — only the last-resort
 * wall inference is gated.
 */
export function runCabinetryMembership(stored) {
  const bayIds = new Set(baysForRun(stored).map((b) => b.id));
  const wallIds = new Set(
    (stored?.segments || []).map((sg) => sg.wallId).filter(Boolean)
  );
  return (o) => {
    if (!isCabinetObject(o)) return false;
    // By id OR alias: a run that absorbed another at a corner join (or a
    // per-wall auto run re-keyed) still owns objects tagged with the old id —
    // `objectsAfterRunResolve` already sweeps them as the run's own via
    // `runAnswersTo`, so membership must agree or a Run-scope edit (and the
    // under-cabinet light) skipped exactly the objects the re-solve replaces.
    if (o?.runId) return runAnswersTo(stored, o.runId);
    if (o?.bayId && bayIds.has(o.bayId)) return true;
    if (isVanityAssemblyGroupId(o?.fixtureGroupId)) return false;
    // Any kind of ref: a generated surround names a REAL wall, and a
    // peninsula's finished back names its ARM — the run's peninsula segment —
    // which no run owns, so this is how a run recolour reaches it.
    const refIds = [
      ...(o?.parentWallIds || []),
      ...(o?.cabinetWallRefs || []).filter((rf) => rf?.id).map((rf) => rf.id),
    ];
    return refIds.some((id) => wallIds.has(id));
  };
}

/**
 * Apply an appearance bundle {doorStyle,hardwareType,handleFinish,paintColor}
 * at a scope and return the full room mutation { objects, cabinetRuns,
 * cabinetAppearance? }. Overwrites that scope and clears narrower overrides:
 *   - 'unit' → the selected bay's override carries the bundle (that bay only).
 *   - 'run'  → the bay's run.appearance; clears that run's per-bay appearance.
 *   - 'room' → room.cabinetAppearance; clears every run/override appearance and
 *              direct-writes the bundle onto every cabinet incl. the generator's.
 * Returns null if the scope can't resolve (e.g. run/unit on a non-run cabinet).
 */
export function applyAppearanceAtScope(
  room,
  scope,
  bay,
  bundle,
  openings = []
) {
  const runId = bay?.runId;
  const runs = room.cabinetRuns || [];

  if (scope === 'unit') {
    // A cabinet in no run: the OBJECT is the unit. A "unit override" is a BAY
    // override — a record on a run — so there is nowhere on a run to put this
    // value, but per-object appearance is already expressible (cabinets across
    // dev carry paintColor / doorStyle / handleFinish directly) and nothing
    // re-solves a run-less cabinet, so its own fields simply render.
    //
    // Returning `cabinetRuns` UNCHANGED is load-bearing: `useBayOverrideApply`
    // writes `cabinetRuns: res.cabinetRuns` unconditionally, so omitting it
    // would write `undefined` and wipe every run in the room.
    if (!runId) {
      if (!bay?.id) return null;
      return {
        objects: (room.objects || []).map((o) =>
          o.id === bay.id ? { ...o, ...bundle } : o
        ),
        cabinetRuns: runs,
      };
    }
    // A corner cabinet isn't an auto bay — its Unit-scope appearance rides on a
    // cornerIndex-keyed override, not a wall-param-anchored one.
    const res =
      bay?.cornerIndex != null
        ? applyCornerOverride(
            room,
            runId,
            bay,
            { appearance: bundle },
            openings
          )
        : applyBayOverride(room, runId, bay, { appearance: bundle }, openings);
    if (!res) return null;
    return {
      // Same class rule as run scope: a Unit-scope pick ON a retained body
      // (pantry, carried corner) writes the override, but the fill never
      // re-emits that object — stamp the picked body directly.
      // `res.resolve` is not optional: it is what moves the run's appliance
      // objects onto their solved bays. Without it a clamped bay's cabinet is
      // re-emitted at the solved centre while the measured appliance stays put,
      // so the first Unit-scope edit of a scanned kitchen slides a sink base off
      // its basin. Every other scope passes it; this one didn't.
      objects: objectsAfterRunResolve(
        room,
        runId,
        res.objects,
        res.resolve
      ).map((o) =>
        o?.id === bay.id && isCabinetObject(o)
          ? keepGlass(o, { ...o, ...bundle })
          : o
      ),
      cabinetRuns: runs.map((r) => (r.id === runId ? res.storedRun : r)),
    };
  }

  if (scope === 'run') {
    const stored = runs.find((r) => r.id === runId);
    if (!stored) return null;
    const newStored = {
      ...stored,
      appearance: bundle,
      // Strip by DESTRUCTURE — a literal `appearance: undefined` persists
      // into the stored overrides and Firestore rejects the room write.
      bayOverrides: overridesForRun(stored).map(
        ({ appearance: _clearAppearance, ...o }) => o
      ),
    };
    delete newStored.pins;
    const run = rehydrateRun(newStored, room);
    const out = materializeRunObjects(run, room, openings);
    return {
      // Retained-but-never-re-emitted cabinetry keeps its OLD finish unless
      // stamped directly: anchored tall units, carried-forward corner
      // variants the fill can't rebuild, and drop-added appliance surrounds
      // (runId deliberately stripped — they carry the run's bayId instead).
      // One rule for the class (browser test 2, 2026-08-31). Idempotent on
      // freshly-emitted objects — they already wear this bundle.
      objects: (() => {
        const member = runCabinetryMembership(newStored);
        return objectsAfterRunResolve(room, runId, out.objects, out).map((o) =>
          member(o) ? keepGlass(o, { ...o, ...bundle }) : o
        );
      })(),
      cabinetRuns: runs.map((r) => (r.id === runId ? newStored : r)),
    };
  }

  // scope === 'room'
  const cabinetRuns = runs.map((r) => {
    const next = {
      ...r,
      appearance: undefined,
      bayOverrides: overridesForRun(r).map((o) => ({
        ...o,
        appearance: undefined,
      })),
    };
    delete next.pins;
    return next;
  });
  // Every run in the room re-solves, and the ONE sweep decides what of the
  // room's own objects survives (RP11). Re-materializing each run and
  // concatenating — with a hand-rolled `!o.runId` filter in front of it — is
  // what deleted a scan run's measured uppers on a room-wide restyle: the
  // filter dropped every runId-tagged object and the re-solve, which keeps
  // scanned uppers rather than rebuilding them, never emitted them back.
  // …keeping the glass the fill drew: the bundle's style goes under it.
  const restyle = (o) =>
    isCabinetObject(o) ? keepGlass(o, { ...o, ...bundle }) : o;
  const roomForMat = { ...room, cabinetAppearance: bundle };
  const resolves = [];
  let emitted = [];
  for (const r of cabinetRuns) {
    // Same guard as the drop target: re-materializing a stale run would build
    // its whole superseded spec — full-wall fill and countertop — over the
    // kitchen that replaced it, on nothing more than a restyle. DERIVED runs
    // only: a hand-drawn run whose cabinets a layout apply swept is rebuilt
    // here on purpose — this is the path that brings it back.
    if (isStaleDerivedRun(r, room.objects)) continue;
    const run = rehydrateRun(r, room);
    if (!run.segments.length) {
      // Never delete what you cannot rebuild. A run whose walls do not resolve
      // — a legacy frameless MANUAL segment, on an environment where
      // `audit-run-frames --write-frames` has not run — has nothing to
      // re-materialize (I1, #1786). Hand its objects through as its own
      // emitted list, restyled, so the sweep keeps them.
      const carried = (room.objects || [])
        .filter((o) => o.runId === r.id)
        .map(restyle);
      resolves.push({ run, objects: carried, bays: [], removedObjectIds: [] });
      emitted = [...emitted, ...carried];
      continue;
    }
    const out = materializeRunObjects(run, roomForMat, openings);
    resolves.push(out);
    emitted = [...emitted, ...out.objects.map(restyle)];
  }
  // The restyle over `room.objects` reaches the generator's run-less cabinets
  // exactly as the old first line did — and an incumbent the sweep re-seats
  // out of that list (a sink host, a tall unit) is restyled with them.
  const merged = mergeResolvedRuns(
    { ...room, objects: (room.objects || []).map(restyle) },
    { resolves, emitted },
    RESOLVE_SITES.EDIT,
    { settleGroups: resolveGroupsAfterMove }
  );
  reportMergeDiagnostics(merged);
  let objects = resolves.reduce(
    (objs, r) => stampRunReadback(objs, r.run),
    merged.objects
  );
  // Final class-wide stamp: retained bodies that ride through a run's
  // re-materialize verbatim (carried corner variants) must adopt the room
  // bundle too. Idempotent on everything freshly emitted.
  objects = objects.map(restyle);
  return { objects, cabinetRuns, cabinetAppearance: bundle };
}

/** An appearance-only override travels with the run, not a wall param. */
function isAppearanceOnlyOverride(o) {
  return (
    o?.appearance != null &&
    o.width == null &&
    (o.type == null || o.type === 'standard') &&
    o.cornerIndex == null
  );
}

/**
 * Scoped regen-merge (regen-merge policy). Given the ids of walls whose geometry
 * changed (moved / resized / opening added), re-solve the runs on those walls
 * that track them (every hand-drawn or generated run, and a scan run once
 * edited) and leave every other run's objects verbatim.
 * A hand-drawn run tracks its wall too, keeping its drawn extent. Within a
 * re-solved run:
 *   - the segment span is refreshed to the current wall length (so a resized wall
 *     reflows);
 *   - appearance overrides are geometry-independent → always kept;
 *   - structural overrides (fixed width / type) are kept by their stored anchor
 *     if it still lands on the wall, else dropped and surfaced as a warning;
 *   - an edited run stays `userEdited`; a pristine run re-solves as `auto`.
 *   - a scan run participates only once edited (`regeneratesOnWallMove`).
 *
 * Never blanket-sweeps user edits — untouched runs (and non-run objects) pass
 * through unchanged.
 *
 * @returns {{ objects, cabinetRuns, warnings, affectedRunIds: string[] }}
 */
export function regenerateAffectedRuns(
  roomInput,
  changedWallIds,
  openings = [],
  opts = {}
) {
  // The walls as they were before the move, by engine id — so a re-seated
  // appliance keeps its offset from the wall's face (see moveAppliances).
  const prevWallsById = opts.prevWalls
    ? buildSegmentWallResolver({ ...roomInput, walls: opts.prevWalls }).wallById
    : null;
  // Legacy runs heal on the way through: per-wall auto runs join at their
  // corner (#1494) and segments move onto the room-wall frame — this is the
  // room-level rewrite that can carry the healed runs and the re-tagged
  // objects back to the store.
  const room = healPersistedRuns(roomInput).room;
  const changed = new Set(changedWallIds || []);
  const runs = room.cabinetRuns || [];
  const resolver = buildSegmentWallResolver(room);
  const warnings = [];
  const affectedRunIds = new Set();

  // 1. Refresh spans + prune un-remappable structural overrides on affected runs.
  const nextRuns = runs.map((run) => {
    // Deliberately NOT gated on `isStaleDerivedRun`: an auto run whose wall
    // shrank to nothing owns no objects until the wall comes back, and only
    // this regen brings it back. A stale AUTO run cannot reach here — the
    // server drops one on reprocess and replaces the set on apply — and a
    // stale SCAN run never regenerates (`regeneratesOnWallMove` wants
    // `userEdited`). A HAND-DRAWN run is never stale by that predicate at all
    // (`isStaleDerivedRun` answers false for it by construction), and it must
    // regenerate here or a moved wall orphans it — so the gate would be wrong
    // for the one class the branch added to this regen.
    const affected =
      regeneratesOnWallMove(run) &&
      (run.segments || []).some((s) => s.wallId && changed.has(s.wallId));
    if (!affected) return run;
    affectedRunIds.add(run.id);

    // The params are metres from the wall's OWN `bottomLeft`, so a move that
    // shifts that origin renames every point on the run. Re-express them in
    // the wall's CURRENT frame BEFORE the extent map clamps them — otherwise
    // a wall grown at its start slides the whole run (and its overrides) by
    // the growth, into the stretch the wall just gained. The measured frame is
    // the wall as it was before the move; failing that, the segment's own
    // stamp. `project` because we already KNOW the wall moved: a perpendicular
    // translation is the move, not an orphan.
    const rebased = rebaseRunFrames(
      run,
      (s) =>
        prevWallsById?.get(s.wallId) ??
        (s.wallFrame && {
          alignment: { bottomLeft: s.wallFrame.a, bottomRight: s.wallFrame.b },
        }) ??
        null,
      (s) => resolver.resolve(s),
      { project: true }
    ).run;

    const segments = (rebased.segments || []).map((s) => {
      if (s.freestanding) return s;
      const wall = resolver.resolve(s);
      const len = wall ? getWallAxes(wall)?.wallLen : null;
      if (len == null) return s;
      const reversed = s.startParam > s.endParam;
      const spans = Math.abs((s.endParam ?? 0) - (s.startParam ?? 0));
      const partial = spans > 0 && spans < len - PARTIAL_EXTENT_EPS;
      // A USER-EDITED extent is preserved (clamped to the new wall), never
      // ballooned to the full span; only a pristine derived run refreshes to
      // full length. Direction — a corner-joined reversed leg — survives
      // both paths (#1525).
      //
      // A run whose extent is NARROWER than its wall is preserved the same way,
      // edited or not. `cabinetLayoutToRuns` now persists the extent the
      // generator actually filled, and the generator stops short of corridor
      // rectangles, the island reserve, wall-body blockers and zone
      // projections. Ballooning that back to the full wall on a wall move
      // filled exactly the stretch the layout had deliberately left empty —
      // which is what "refreshes to full length" meant when every derived run
      // was full-wall by construction.
      //
      // A hand-drawn run is authored: its extent is the user's (ruling A,
      // spec §11) and is only ever clamped.
      if (rebased.userEdited || partial || !isDerivedRun(rebased)) {
        const clamp = (v) => Math.min(Math.max(v ?? 0, 0), len);
        const q0 = clamp(s.startParam);
        const q1 = clamp(s.endParam);
        return q0 === s.startParam && q1 === s.endParam
          ? s
          : { ...s, startParam: q0, endParam: q1 };
      }
      return reversed
        ? { ...s, startParam: len, endParam: 0 }
        : { ...s, startParam: 0, endParam: len };
    });
    const maxLen = segments.reduce(
      // A reversed leg carries its extent in startParam.
      (m, s) => Math.max(m, s.startParam || 0, s.endParam || 0),
      0
    );
    const bayOverrides = (rebased.bayOverrides || []).filter((o) => {
      if (isAppearanceOnlyOverride(o)) return true; // travels with the run
      if (o.cornerIndex != null) return true; // corner overrides aren't param-anchored
      const inRange =
        o.anchorParam != null && o.anchorParam >= 0 && o.anchorParam <= maxLen;
      if (!inRange) {
        warnings.push(
          `An edit on this run no longer fits after the wall changed and was dropped.`
        );
      }
      return inRange;
    });
    // An appliance bay that fell off the shortened wall is SET ASIDE, never
    // deleted: the object stays where it is, the bay is marked and the packer
    // ignores it until the wall (or the bay) moves back.
    const bays = baysForRun(rebased).map((b) => {
      if (!isApplianceKind(b.kind)) return b;
      const fits = b.anchorParam >= 0 && b.anchorParam <= maxLen;
      if (fits) return b.unplaced ? { ...b, unplaced: false } : b;
      if (!b.unplaced) {
        warnings.push(
          `An appliance on this run no longer fits after the wall changed and was set aside.`
        );
      }
      return { ...b, unplaced: true };
    });
    return stampStoredRun(
      storedRunFromRun({ ...rebased, segments, bayOverrides, bays }),
      room
    );
  });

  // 2. Re-solve affected runs; keep everything else verbatim.
  const resolvedObjects = [];
  const resolves = [];
  for (const run of nextRuns) {
    if (!affectedRunIds.has(run.id)) continue;
    const inMem = rehydrateRun(run, room);
    if (!inMem.segments.length) continue; // wall gone — drop its objects
    const resolve = materializeRunObjects(inMem, room, openings);
    resolvedObjects.push(...resolve.objects);
    resolves.push(resolve);
    if (resolve.warnings?.length) warnings.push(...resolve.warnings);
  }

  const objects = objectsAfterRunsResolve(
    room,
    affectedRunIds,
    resolvedObjects,
    resolves,
    prevWallsById ? { prevWallsById } : {}
  );
  return {
    objects,
    cabinetRuns: nextRuns,
    warnings,
    affectedRunIds: [...affectedRunIds],
  };
}

/**
 * After an override re-solve, pick the run cabinet to re-select so the panel
 * stays on the same bay: prefer the one tagged with `overrideId`, else the run
 * cabinet on the same wall nearest the edited bay's centre. Returns null if
 * there's nothing to select.
 */
export function pickReselectBay(objects, bay, overrideId) {
  // The box's own TIER first. An upper and the lower beneath it share a wall
  // and a centre in plan, and a corner upper shares its `cornerIndex` with the
  // corner base — so a search that ignores the tier hands back the wrong one
  // of the pair. It used to search `lower-cabinet`s only, which was the whole
  // population while only lowers could be edited: a width edit on an upper
  // (whose id is minted from its start param, so it changes) dropped the panel
  // onto the base cabinet underneath.
  const upper = bayTier(bay) === 'upper';
  const sameTier = (o) => (bayTier(o) === 'upper') === upper;
  // A corner cabinet re-selects by its stable cornerIndex: it isn't a
  // lower-cabinet auto bay and shares a wall with the straight legs, so the
  // wall + nearest-centre matching below would grab a neighbour instead.
  if (bay?.cornerIndex != null) {
    return (
      (objects || []).find(
        (o) =>
          o.type === 'corner-cabinet' &&
          o.cornerIndex === bay.cornerIndex &&
          sameTier(o)
      ) || null
    );
  }
  const wallId = bay?.parentWallIds?.[0];
  const straightType = upper ? 'upper-cabinet' : 'lower-cabinet';
  // A stacked upper stands over its upper at the same centre in plan, and is
  // never a bay of its own (`category.stack`, plan 2026-10-21, C21).
  const onWall = (objects || []).filter(
    (o) =>
      o.type === straightType &&
      (o.parentWallIds || [])[0] === wallId &&
      !o.category?.stack
  );
  if (!onWall.length) return null;
  const d2 = (o) =>
    (o.center.x - bay.center.x) ** 2 + (o.center.z - bay.center.z) ** 2;
  // An override id only when there IS one: with none, `undefined ===
  // undefined` matched the first cabinet on the wall, so a re-select after a
  // re-pack jumped to the run's far end rather than the nearest cabinet.
  return (
    (overrideId != null && onWall.find((o) => o.overrideId === overrideId)) ||
    onWall.reduce((b, o) => (!b || d2(o) < d2(b) ? o : b), null)
  );
}

/**
 * Apply a patch ({ depth?, sided?, cookSide? }) to a free-standing segment and
 * re-materialize its run. Returns { objects, cabinetRuns } or null.
 *
 * `{ flipSides: true }` is Flip A/B: the WHOLE island turned round
 * (`flipStoredRunSide`, design 2026-11-03), or `{ refused }` with the reason
 * the button is greyed with when a wall leg decides the run's side.
 *
 * `freeSegIndex` is the index in the stored run's segments array (as tagged on
 * materialized objects by `materializeRunObjects`).
 */
export function applyFreestandingEdit(
  room,
  runId,
  freeSegIndex,
  patch,
  openings = []
) {
  const stored = (room.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored) return null;
  const seg = stored.segments?.[freeSegIndex];
  if (!seg?.freestanding) return null;

  let newStored;
  if (patch?.flipSides === true) {
    const flip = flipStoredRunSide(stored, room);
    if (flip.refused) return { refused: flip.refused };
    newStored = flip.storedRun;
  } else {
    const newSegments = stored.segments.map((s, i) =>
      i === freeSegIndex && s.freestanding ? { ...s, ...patch } : s
    );
    newStored = { ...stored, segments: newSegments };
  }
  const run = rehydrateRun(newStored, room);
  const out = materializeRunObjects(run, room, openings);
  return {
    objects: objectsAfterRunResolve(room, runId, out.objects, out),
    cabinetRuns: (room.cabinetRuns || []).map((r) =>
      r.id === runId ? newStored : r
    ),
  };
}

/** The `upperSpec` fields and what each must be to be stored. */
const UPPER_SPEC_FIELDS = {
  gap: (v) => Number.isFinite(v) && v > 0,
  height: (v) => Number.isFinite(v) && v > 0,
  anchor: (v) => v === 'ceiling',
  align: (v) => v === 'independent',
  // glass doors on every upper the line makes one for (`glassUppers`)
  glass: (v) => v === true,
  // a stacked row on the uppers (`upperStack.js`): `{ height, glass? }`,
  // `height` a length or 'ceiling', `glass` true or absent — nothing else
  stack: isStackSpec,
};

/** Is `v` a stack exactly as the spec stores one (`upperBand.statedStack`)? */
function isStackSpec(v) {
  if (!v || typeof v !== 'object') return false;
  const keys = Object.keys(v);
  if (!keys.every((k) => k === 'height' || k === 'glass')) return false;
  if (v.glass !== undefined && v.glass !== true) return false;
  if (typeof v.height === 'number' && !Number.isFinite(v.height)) return false;
  return statedStack(v) != null;
}

/**
 * `stored` with `patch` merged into its `upperSpec` — the one write boundary
 * for the spec, or null when the patch is refused. A spec left with nothing
 * in it is removed from the run (an empty object says nothing, and Firestore
 * rejects an `undefined`).
 *
 * A `height` the run's ceiling has no room for is refused (user ruling
 * 2026-10-21, plan C6 — the panel greys it; this guards every other caller).
 * A patch that only moves the band (`anchor`, `gap`) is never refused: a
 * stored height it leaves without room is built at the tallest that fits and
 * warned (`upperBand.upperBandFor`).
 */
function withUpperSpecPatch(room, stored, patch) {
  const spec = { ...(stored.upperSpec || {}) };
  for (const [field, value] of Object.entries(patch)) {
    const valid = UPPER_SPEC_FIELDS[field];
    if (!valid) return null;
    if (value == null) delete spec[field];
    else if (valid(value)) spec[field] = value;
    else return null;
  }
  const { upperSpec: _previous, ...rest } = stored;
  const next = Object.keys(spec).length ? { ...rest, upperSpec: spec } : rest;
  if (patch.height != null) {
    const band = runUpperBand(next, room);
    if (
      !fitsUnderCeiling(roomInches(band.room), patch.height / INCHES_TO_METERS)
    )
      return null;
  }
  // …and a stack the ceiling leaves no room for — a sized one that does not
  // fit whole, "To ceiling" over uppers that already reach it — is refused,
  // as the panel greys it (`upperBand.stackOver`).
  if (patch.stack != null && !runUpperBand(next, room).stack) return null;
  return next;
}

/**
 * Set WHERE a run's uppers hang — `run.upperSpec` — and re-materialize it.
 *
 * `patch` MERGES into the stored spec: `{ anchor: 'ceiling' }` (clamp their
 * tops to the ceiling) OR `{ gap }` (metres above the countertop their
 * bottoms sit — one clamp or the other, `upperBand.js`), and `{ height }`
 * (the cabinet's own height, either way), and `{ align: 'independent' }` (pack
 * the uppers on their own, as equal widths, instead of mirroring the lowers).
 * `null` clears a field back to the standard, and a spec left with
 * nothing in it is removed from the run altogether — an empty object says
 * nothing, and Firestore rejects an `undefined`.
 *
 * The write boundary: a value that is not a real length (or a `top` that is
 * not `'ceiling'` — an `anchor`) is refused here rather than stored, because the engine
 * reads the spec verbatim and substitutes nothing.
 *
 * Same return shape as `applyRunUppers`, which it sits beside in the panel.
 *
 * @returns {{ objects, cabinetRuns }|null}
 */
export function applyRunUpperSpec(room, runId, patch, openings = []) {
  const stored = (room.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored || !patch) return null;
  const newStored = withUpperSpecPatch(room, stored, patch);
  if (!newStored) return null;
  const run = rehydrateRun(newStored, room);
  const out = materializeRunObjects(run, room, openings);
  return {
    objects: objectsAfterRunResolve(room, runId, out.objects, out),
    cabinetRuns: (room.cabinetRuns || []).map((r) =>
      r.id === runId ? newStored : r
    ),
    // what the fill said of the run — the panel tells "N uppers stay solid"
    warnings: out.warnings || [],
  };
}

/**
 * An upper-tier override that says "no cabinet here" (`type: 'none'`) — the
 * one bay edit that removes the very box the user would select to undo it.
 */
const isRemovedUpper = (o) => overrideTier(o) === 'upper' && o?.type === 'none';

/** How many uppers the user has removed from this run — the panel's count. */
export function removedUpperCount(stored) {
  return overridesForRun(stored || {}).filter(isRemovedUpper).length;
}

/** An upper-tier override that opts its box out of the stacked row. */
const optsOutOfStack = (o) =>
  overrideTier(o) === 'upper' && o?.noStack === true;

/** How many uppers on this run stand with nothing stacked over them by the
 * user's choice — the panel's "N spots without a stacked cabinet" (C25). */
export function noStackCount(stored) {
  return overridesForRun(stored || {}).filter(optsOutOfStack).length;
}

/**
 * Put the stacked cabinet back over every upper the user opted out, and
 * re-solve. Only the opt-out is lifted; whatever else a record carries stays,
 * and a record left carrying nothing is dropped by the re-solve.
 *
 * @returns {{ objects, cabinetRuns }|null}
 */
export function restoreStacks(room, runId, openings = []) {
  const stored = (room?.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored) return null;
  const next = overridesForRun(stored).map((o) => {
    if (!optsOutOfStack(o)) return o;
    const { noStack: _optOut, ...rest } = o;
    return rest;
  });
  return resolveRunEdit(room, runId, stored, next, openings);
}

/** An upper-tier override that sets the box's own height (a bridge cabinet). */
const hasUpperBayHeight = (o) => overrideTier(o) === 'upper' && o?.height > 0;

/** How many of this run's uppers set their own height — the panel's count. */
export function upperBayHeightCount(stored) {
  return overridesForRun(stored || {}).filter(hasUpperBayHeight).length;
}

/** The run's overrides with every upper's own height lifted. */
const withoutUpperBayHeights = (stored) =>
  overridesForRun(stored).map((o) => {
    if (!hasUpperBayHeight(o)) return o;
    const { height: _own, ...rest } = o;
    return rest;
  });

/** Re-solve `stored` with `overrides` and package it as the run edits do. */
function resolveRunEdit(room, runId, stored, overrides, openings) {
  const res = rematerializeWithOverrides(
    stored,
    room,
    openings,
    overrides,
    null
  );
  return {
    objects: objectsAfterRunResolve(room, runId, res.objects, res.resolve),
    cabinetRuns: (room.cabinetRuns || []).map((r) =>
      r.id === runId ? res.storedRun : r
    ),
  };
}

/**
 * Lift every upper's own height on a run — the run panel's "Reset to N"" —
 * and re-solve. Fronts, widths and appearances stay; a record left carrying
 * nothing is dropped by the re-solve.
 *
 * @returns {{ objects, cabinetRuns }|null}
 */
export function resetUpperBayHeights(room, runId, openings = []) {
  const stored = (room?.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored) return null;
  return resolveRunEdit(
    room,
    runId,
    stored,
    withoutUpperBayHeights(stored),
    openings
  );
}

/**
 * Put every removed upper on a run back and re-materialize it.
 *
 * Only the `none` is lifted: a record that also fixes a width or carries an
 * appearance keeps both, so a fixed-width gap comes back as the fixed-width
 * cabinet it was. A record left carrying nothing is dropped by the
 * re-materialize, like any other override that no longer does anything.
 *
 * Same return shape as `applyRunUppers`, which it sits beside in the panel.
 */
export function restoreRemovedUppers(room, runId, openings = []) {
  const stored = (room.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored) return null;
  const next = overridesForRun(stored).map((o) => {
    if (!isRemovedUpper(o)) return o;
    const { type: _none, ...rest } = o;
    return rest;
  });
  const res = rematerializeWithOverrides(stored, room, openings, next, null);
  return {
    objects: objectsAfterRunResolve(room, runId, res.objects, res.resolve),
    cabinetRuns: (room.cabinetRuns || []).map((r) =>
      r.id === runId ? res.storedRun : r
    ),
  };
}

/**
 * What each corner of a run holds, tier by tier, and which answers fit there —
 * read off a solve, so the panel offers exactly what the fill would build
 * (`@shared-lib/cabinet-runs/lowerCornerTreatment`, `…/upperCornerTreatment`).
 * Nothing is committed. One entry per corner per tier, the base first; a run
 * with its uppers off reports only its bases, and one with no corner `[]`.
 */
export function readRunCorners(room, runId, openings = []) {
  const stored = (room?.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored || (stored.segments || []).length < 2) return [];
  const out = materializeRunObjects(rehydrateRun(stored, room), room, openings);
  return [
    ...(out.lowerCorners || []).map((c) => ({ ...c, tier: 'lower' })),
    ...(out.upperCorners || []).map((c) => ({ ...c, tier: 'upper' })),
  ];
}

/**
 * Set what the user says a corner of a run holds — or, with
 * `treatment: null`, hand it back to the layout ("Auto") — and re-materialize.
 *
 * The choice rides on the corner's own per-tier override, beside its
 * appearance: one record per corner per tier (`applyCornerOverride` writes the
 * same one), so clearing the choice leaves an appearance edit standing and an
 * empty record is dropped.
 *
 * WHICH FIELD is the tier's (`cornerChoice.userChoiceField`). A lower record's
 * `treatment` is the generator's and is never written here: the pick sits
 * beside it, so "Auto" puts back exactly the corner the generator built.
 *
 * Returns { objects, cabinetRuns } or null.
 */
export function setCornerTreatment(
  room,
  runId,
  { cornerIndex, tier, treatment },
  openings = []
) {
  const stored = (room?.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored || (tier !== 'upper' && tier !== 'lower')) return null;
  if (!Number.isInteger(cornerIndex) || cornerIndex < 0) return null;
  const field = userChoiceField(tier);
  const overrides = overridesForRun(stored);
  const at = overrides.findIndex(
    (o) => o.cornerIndex === cornerIndex && overrideTier(o) === tier
  );
  let next;
  if (treatment) {
    const id = at >= 0 ? overrides[at].id : cornerOverrideId(tier, cornerIndex);
    next =
      at >= 0
        ? overrides.map((o, i) =>
            i === at ? { ...o, tier, [field]: treatment } : o
          )
        : [
            ...overrides.filter((o) => o.id !== id),
            { id, cornerIndex, tier, [field]: treatment },
          ];
  } else {
    if (at < 0 || overrides[at][field] == null) return null;
    next = overrides.map((o, i) => {
      if (i !== at) return o;
      const { [field]: _auto, ...rest } = o;
      return rest;
    });
  }
  const res = rematerializeWithOverrides(stored, room, openings, next, null);
  return {
    objects: objectsAfterRunResolve(room, runId, res.objects, res.resolve),
    cabinetRuns: (room.cabinetRuns || []).map((r) =>
      r.id === runId ? res.storedRun : r
    ),
  };
}

/**
 * Toggle a run's upper cabinets on/off and re-materialize it. Sets
 * `run.uppers` on the stored run (default-ON model: only an explicit `false`
 * suppresses uppers) and re-solves, replacing that run's materialized objects.
 * Returns { objects, cabinetRuns } or null. Wall-backed runs only — the
 * free-standing path never emits uppers, so this is a no-op there.
 *
 * "Rebuilt" on a SCANNED run that states no `upperSpec` of its own writes the
 * height and gap its measured uppers have (`upperBand.scannedUpperSpec`, plan
 * 2026-10-21 C8) — it used to rebuild measured 36" or 42" uppers at the
 * standard 30", since an empty spec builds the standard. A spec the run
 * already states is the user's, and is never overwritten.
 */
export function applyRunUppers(room, runId, on, openings = []) {
  const stored = (room.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored) return null;
  const floorY = getFloorY(room);
  // Only WHERE the uppers hang counts as the run's own say here — a spec that
  // says only "glass" still takes the measured height and gap, beside it.
  const statesBand = (spec) => {
    const stated = statedUpperSpec(spec);
    return !!stated && ['anchor', 'gap', 'height'].some((k) => k in stated);
  };
  const measured =
    on === true && stored.source === 'scan' && !statesBand(stored.upperSpec)
      ? scannedUpperSpec(stored, room.objects, {
          floorY,
          ceilingY: roomCeilingY(room, floorY),
        })
      : null;
  const newStored = {
    ...stored,
    uppers: on,
    ...(measured
      ? { upperSpec: { ...(stored.upperSpec || {}), ...measured } }
      : {}),
  };
  const run = rehydrateRun(newStored, room);
  const out = materializeRunObjects(run, room, openings);
  return {
    objects: objectsAfterRunResolve(room, runId, out.objects, out),
    cabinetRuns: (room.cabinetRuns || []).map((r) =>
      r.id === runId ? newStored : r
    ),
  };
}

/**
 * The bay that owns WHERE THIS OBJECT SITS: the bay that claims the object
 * itself, else the bay of the appliance it is INSTALLED ON — a kitchen faucet
 * rides on its basin, and the basin's bay decides where both of them stand.
 *
 * The one loophole this closes: a faucet carries no `runId` and no `bayId`,
 * and no bay names it, so every movement surface read it as a loose object
 * and free-moved it together with its `getMoveGroup` closure — which reaches
 * the basin, the sink cabinet and (through that cabinet's `parents` entry on
 * the countertop) the run's countertop, while the run's own cabinets stayed
 * put. Nudging the faucet tore the island in half.
 *
 * The host is read from `parents[]` ONLY. That array names a specific host —
 * "compute my position from this object", `kitchen-assembly/fixtureGroup.js` —
 * while `fixtureGroupId` names a set, and on a generated island that set spans
 * the sink bay AND the dishwasher bay, so a group fallback would inherit
 * whichever bay came first. An object a RUN owns is excluded before the
 * lookup: the run re-emits it, and a run-spanning countertop lists every
 * cabinet in the run as a parent — one of which can be a sink base carrying
 * the bay stamp, which would make the countertop itself look bay-anchored.
 *
 * Movement surfaces only (the Position arrows, the bay drag, the free-move
 * refusal). `runBayForObject` stays the IDENTITY matcher, because a link's
 * subject, a bay's own editor and a run re-solve are about the appliance.
 *
 * @returns {{ stored, bay }|null}
 */
export function runBayAnchoring(room, object) {
  const own = runBayForObject(room, object);
  if (own) return own;
  if (!object || object.runId) return null;
  for (const parent of object.parents || []) {
    if (!parent?.id || parent.id === object.id) continue;
    const host = (room?.objects || []).find((o) => o?.id === parent.id);
    const hit = host ? runBayForObject(room, host) : null;
    if (hit) return hit;
  }
  return null;
}

/**
 * Did an edit change the SIZE of this object — its dimensions beyond 1 mm,
 * or its model? Position, rotation, name and attributes are not sizes: a run
 * already owns an appliance's position (the 2D bay drag), and a re-solve on
 * every nudge would snap a 3D free move straight back to its bay.
 */
/**
 * The bay an object slides IN and the wall it slides ALONG:
 * `{ stored, bay, wall }`, or null when no bay anchors the object.
 *
 * Resolved through `runBayAnchoring`, so an accessory answers with its host
 * appliance's bay — a faucet slides where its basin slides.
 *
 * The wall is what makes a bay SLIDEABLE — it is the axis of the along-run
 * arrows and of the nudge itself. A wall-backed segment resolves its own
 * through `rehydrateRun`, aliases and the room-wall frame included; a
 * FREE-STANDING leg has no wall, so it answers with the virtual wall
 * `resolveFreeSegment` builds — the same line the fill packs against, cook
 * side resolved the way `materializeRunObjects` resolves it, so the axis the
 * user aims along and the axis the bay moves along are one line.
 *
 * Null only when neither resolves: a segment whose wall is orphaned, or a
 * free leg with no endpoints.
 */
export function runBaySlide(room, object) {
  const hit = runBayAnchoring(room, object);
  if (!hit) return null;
  const wall = runSegmentWall(room, hit.stored, hit.bay.segmentIndex ?? 0);
  return wall ? { ...hit, wall } : null;
}

/**
 * The wall a segment's params are measured along — its own for a wall-backed
 * segment, the virtual line `resolveFreeSegment` builds for a free-standing
 * leg. ONE answer, shared by the Position arrows (`runBaySlide`) and the
 * canvas bay drag (`useRunDrag`), so the axis the arrow points along and the
 * axis the pointer is projected onto can never be two different lines.
 *
 * @returns {Object|null} an engine wall, or null when neither resolves.
 */
export function runSegmentWall(room, stored, segIndex) {
  const run = rehydrateRun(stored, room);
  return (
    run.segments?.[segIndex]?.wall ||
    resolveFreeSegment(stored, segIndex, room)?.wall ||
    null
  );
}

/**
 * A vanity run's sink bays, re-asked at the run's current sizing intent.
 *
 * Returns the run's bays unchanged for a kitchen run, for a run with no sink
 * bay, or wherever the geometry cannot be resolved — a bay this cannot
 * measure keeps the width it has rather than being guessed at.
 */
function resizedVanitySinkBays(stored, room) {
  const bays = stored.bays || [];
  if (profileOf(stored).id !== VANITY_PROFILE_ID || !bays.length) return bays;
  const sinks = bays.filter((b) => b?.kind === 'sink-base');
  if (!sinks.length) return bays;
  const objects = room?.objects || [];
  // The RUN's own extent, not `bayCenterSpan` — that one insets the segment by
  // half the bay's width to bound its CENTRE, which is a different question and
  // would shrink the answer by a bay every pick.
  const seg = (stored.segments || [])[sinks[0].segmentIndex ?? 0];
  if (!seg || seg.startParam == null || seg.endParam == null) return bays;
  const lo = Math.min(seg.startParam, seg.endParam);
  const hi = Math.max(seg.startParam, seg.endParam);
  // GOING BACK TO AS BUILT MEANS THE BOWLS TOO. A standard pick re-seats them
  // to their cabinets' middles (product directive 2026-09-27), and nothing but
  // `run.measured.basins` remembers where the scan had them — so without this
  // the depth chip is a one-way door for the basins, which is exactly the
  // property the "As built" chip exists to guarantee.
  const restoring = sizingOf(stored) === AS_BUILT;
  const scanned = new Map(
    (stored.measured?.basins || [])
      .filter((b) => b && Number.isFinite(b.centerParam))
      .map((b) => [b.id, b.centerParam])
  );
  const measureBasin = (bay, basin, id) => {
    // By the BASIN's id — `measured.basins` is keyed that way, because a bay
    // id is derived and a re-derive could mint a different one.
    const restored = restoring ? scanned.get(basin?.id) : undefined;
    // The wall the bay's SEGMENT stands on. A stored bay carries no
    // `parentWallIds` — `bayWallId` reads an OBJECT's — so asking it here
    // found no wall, and every bowl was read at its bay's anchor: the box's
    // middle, which for the line's double-bowl unit is BETWEEN its bowls.
    const wallId = stored.segments?.[bay.segmentIndex ?? 0]?.wallId ?? null;
    return {
      id,
      bowl: basin?.dimensions?.x,
      // Where the bowl BELONGS: what the scan recorded when we are going back
      // to as-built, and otherwise where the bowl actually stands. Read from
      // the BASIN rather than from the bay we are replacing, whose anchor is
      // the box's middle and not the bowl's.
      centerParam: Number.isFinite(restored)
        ? restored
        : basin
          ? (runWallParamForPoint(stored, wallId, basin.center, room) ??
            bay.anchorParam)
          : bay.anchorParam,
    };
  };
  const measured = sinks.flatMap((bay) => {
    const basin = objects.find((o) => o?.id === bay.applianceId);
    const own = measureBasin(bay, basin, bay.id);
    // A line's DOUBLE-BOWL unit is one bay holding two bowls (2026-10-21):
    // the rule has to be asked about BOTH, or it re-plans the vanity as one
    // bowl and leaves the other standing outside its cabinet. The second has
    // no bay of its own, so it is named by the bay its own derive would have
    // given it.
    const others = bowlsOfSinkBay(bay, objects)
      .filter((o) => o.id !== bay.applianceId && o.center)
      .map((o) => measureBasin(bay, o, `bay_${o.id}`));
    return [own, ...others];
  });
  // A bay whose basin cannot be measured keeps the width it has rather than
  // being guessed at — and it keeps its NEIGHBOURS' too, since the rule divides
  // the run between the bowls and a missing bowl moves every boundary.
  if (measured.some((m) => !(m.bowl > 0) || !Number.isFinite(m.centerParam))) {
    return bays;
  }
  const placed = new Map(
    vanitySinkBays({
      basins: measured,
      lo,
      hi,
      sizing: sizingOf(stored),
      run: stored,
      // This IS the user's answer, so the standard layout may re-seat a
      // basin (product directive 2026-09-27) — a derive may not.
      fromPick: true,
    }).map((p) => [p.id, p])
  );
  // The rule answers in UNITS: a bowl it now puts in a unit of its own, or a
  // bay it no longer answers for, is a different vanity than this run's bays
  // describe — keep the bays as they are rather than half-rewrite them.
  const sinkIds = new Set(sinks.map((b) => b.id));
  if (
    [...placed.keys()].some((id) => !sinkIds.has(id)) ||
    sinks.some((b) => !placed.has(b.id))
  ) {
    return bays;
  }
  return bays.map((bay) => {
    const next = placed.get(bay?.id);
    if (!next) return bay;
    const out = { ...bay, width: next.width, anchorParam: next.centerParam };
    // …and how many bowls its unit holds (the line's double-bowl unit).
    if (next.sinkCount > 1) out.sinkCount = next.sinkCount;
    else delete out.sinkCount;
    // The bowl's own seat, where it differs from the box's middle. Cleared
    // rather than left stale when the pick hands the lead to the cabinet.
    if (Number.isFinite(next.applianceParam)) {
      out.applianceParam = next.applianceParam;
    } else {
      delete out.applianceParam;
    }
    return out;
  });
}

/**
 * The stored run rebuilt at `depth`: the depth, the counter overhang that
 * belongs to it, the sizing answer the pick gives and the sink bays re-asked
 * at that answer. Null when nothing would change. Pure — `applyRunDepth`
 * re-solves it, and a family switch into a line that builds one depth only
 * (`applyRunFamily`) brings a run to that depth through it.
 */
function storedRunAtDepth(stored, depth, room) {
  // A sixteenth of a millimetre — two lengths the user would call the same.
  const current = runDepthSpec(stored);
  // A no-op is "the same depth AND the same answer". Those come apart wherever
  // the measurement coincides with an offered depth — every vanity the builder
  // built, whose measured depth IS the profile's standard — and there the
  // depth-only guard made the control DEAD: the run read as as-built with a
  // full-width sink base while the panel showed Standard selected, and picking
  // Standard changed nothing because the number was already right.
  const isVanity = profileOf(stored).id === VANITY_PROFILE_ID;
  const nextSizing =
    stored.measured?.depth != null &&
    Math.abs(stored.measured.depth - depth) < 1e-4
      ? AS_BUILT
      : STANDARD;
  const sizingUnchanged = !isVanity || sizingOf(stored) === nextSizing;
  if (Math.abs(current - depth) < 1e-4 && sizingUnchanged) return null;

  // THE OVERHANG BELONGS TO THE CARCASS IT WAS MEASURED AGAINST. A recorded
  // overhang describes the counter the scan found over the carcass the scan
  // found (`vanityToRun`: carcass + overhang = the measured envelope), so the
  // moment the carcass depth moves it describes nothing — and riding along it
  // made "Standard" mean three different vanities depending on what the room
  // had granted: 21.00", 22.25" or 21.98" of counter for one 21" pick.
  //
  // Choosing the measured depth again brings its envelope back with it, so the
  // round trip is exact rather than merely close.
  const newStored = { ...stored, depth };
  const asBuilt = stored.measured;
  const backToAsBuilt = nextSizing === AS_BUILT;
  if (backToAsBuilt && asBuilt.counterOverhang != null) {
    newStored.counterOverhang = asBuilt.counterOverhang;
  } else {
    delete newStored.counterOverhang;
  }
  // THE SINK BASE FOLLOWS THE SAME CHOICE. A bay's width is stored in the
  // run's spec and the fill only reads it, so the rule has to be re-asked
  // here — this is the moment the intent changes (`sizing.js`). As-built is
  // one box the width of the vanity, standard is the narrowest base that
  // clears the bowl with real cabinets beside it; leaving the derived width
  // in place would have "Standard" keep the as-built box and only shrink it.
  // PICKING A DEPTH IS ANSWERING THE QUESTION, so record the answer rather
  // than leaving it to be re-derived from the number: choosing the measured
  // depth is "match what is there", and choosing any offered depth is
  // "standard sizes" (`sizing.js`). Stamped for a vanity only — a kitchen run
  // has no such choice, and writing the field on one would imply it did.
  if (isVanity) newStored.sizing = nextSizing;
  newStored.bays = resizedVanitySinkBays(newStored, room);
  return newStored;
}

/**
 * Set the body DEPTH a run is built at, out of the depths it is offered
 * (`depthOptionsForRun`: its profile's, narrowed to what its cabinet line
 * makes). Persists `run.depth` and re-solves.
 *
 * Always a change of geometry — every body, the countertop over them and the
 * corner box all follow the depth — so unlike the family there is no
 * stamp-only path: either the depth differs and the run re-solves, or nothing
 * happens at all. Picking the depth the run already has is a no-op rather
 * than a re-solve, so it cannot mark a pristine scan run edited for nothing
 * (the same rule `applyRunFamily` follows).
 *
 * The depth is NOT snapped to the offered list on the way in. A run derived
 * from a scan carries its measured depth, and the picker shows that as its
 * current value; only an explicit pick replaces it.
 *
 * Returns null for an unknown run, a non-positive depth, a depth the run's
 * line does not make (a Venus vanity is 21" only), or a run with no
 * wall-backed segments to re-solve.
 */
export function applyRunDepth(room, runId, depth, openings = []) {
  const stored = (room.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored || !(depth > 0)) return null;
  if (!depthInLine(stored, depth)) return null;
  const newStored = storedRunAtDepth(stored, depth, room);
  if (!newStored) return null;
  const cabinetRuns = (room.cabinetRuns || []).map((r) =>
    r.id === runId ? newStored : r
  );
  const roomForMat = { ...room, cabinetRuns };
  const run = rehydrateRun(newStored, roomForMat);
  if (!run.segments.length) return null;
  const out = materializeRunObjects(run, roomForMat, openings);
  const objects = objectsAfterRunResolve(roomForMat, runId, out.objects, out);
  return { objects, cabinetRuns, depth };
}

/**
 * Set the product family a run draws from (#1493). Persists `run.family`,
 * re-stamps `runFamily` on the objects the run owns, and RE-FITS the run to
 * the family's own widths — choosing the family IS the explicit apply (the
 * design's phase-3 preview refines this, it doesn't gate it).
 *
 * Every family decides widths differently now (divided evenly, the 3" grid,
 * or a stock set), so a change of family is always a change of geometry.
 * The re-fit used to be gated on the family having a width LIST, which was
 * right only while the alternative was a family that constrained nothing.
 * Picking the family a run already has still only re-stamps — re-solving a
 * run to the layout it already has would mark a pristine scan run edited for
 * nothing — UNLESS the family is a sized line (Venus) and the run still holds
 * units the line does not make: then the re-solve is the repair.
 *
 * Under a sized line the units the re-solve KEEPS (a scanned pantry, the
 * sink's host, a measured over-fridge cabinet) come back as the nearest unit
 * the line makes — the rule the generator's "match existing" option shares
 * (`@shared-lib/cabinet-runs/keptUnitsInLine`).
 *
 * The look lands inside what the COMPANY offers of the new family too
 * (`companyOffering`, issue #2460), when one is given.
 *
 * Returns null for an unknown family or run.
 */
export function applyRunFamily(
  room,
  runId,
  familyId,
  openings = [],
  companyOffering
) {
  const family = familyById(familyId);
  const stored = (room.cabinetRuns || []).find((r) => r.id === runId);
  if (!family || !stored) return null;
  // A run of a family this build does not know READS as the default family,
  // so "the same family" is a rebuild there too: a stamp-only pick would
  // relabel the other family's boxes without re-fitting them.
  const changed = isUnknownFamily(stored) || familyOf(stored).id !== family.id;
  // A line with a size catalog (Venus) builds only what it makes, the units
  // a re-solve KEEPS included (`keptUnitsInLine`). Re-picking such a line
  // re-solves a run that still holds units it does not make — a run switched
  // before the line could size its install pieces carried a ½" surround at
  // the fridge's depth, and a stamp-only pick could never repair it.
  const sizedLine = !!familySizes(family);
  const refit =
    changed ||
    (sizedLine && runHoldsUnitsOutsideLine(room.objects, runId, family));
  // The LOOK comes along: a switch is chosen, so the run lands on a door and
  // finish the new family sells rather than keeping the old family's
  // (`lookCarriedIntoFamily`). Without this a run switched back from Tasoro
  // kept "Tasoro Shaker" in Champagne under Generic Stock. A repair re-solve
  // carries it too: a run labelled with the line before it was built in it
  // can wear a look the line does not sell, and a look it does sell is kept.
  const relabelled = {
    ...stored,
    family: family.id,
    ...(refit
      ? lookCarriedIntoFamily(room, stored, family, companyOffering)
      : {}),
  };
  // A recorded L corner at a size the new line does not sell is left for the
  // line to size (`cornerSizesInLine`) — the same rule option #1 follows.
  const cornered = refit ? cornerSizesInLine(relabelled) : relabelled;
  // …and a depth it does not build is brought to the one it does: a narrow
  // 15.3" vanity switched into Venus is a 21" Venus vanity (user ruling
  // 2026-11-02), through the depth pick's own rules (`storedRunAtDepth`).
  const newStored =
    refit && !depthInLine(cornered, runDepthSpec(cornered))
      ? storedRunAtDepth(
          cornered,
          depthOptionsForRun(cornered)[0].depth,
          room
        ) || cornered
      : cornered;
  const cabinetRuns = (room.cabinetRuns || []).map((r) =>
    r.id === runId ? newStored : r
  );
  // The line rides on the run's objects (`runFamily`), and on what follows
  // the run without being its — a peninsula's finished back, which bills by
  // its own stamp (`vendorCabinetPricing`). An object with no stamp of its own
  // (a generated surround) keeps billing as it did.
  const member = runCabinetryMembership(newStored);
  const stampFamily = (o) =>
    o?.runId === runId || (!o?.runId && o?.runFamily && member(o))
      ? { ...o, runFamily: family.id }
      : o;
  const carryObjects = (objects) =>
    refit
      ? objectLooksCarriedIntoFamily(
          objects,
          newStored,
          family,
          companyOffering
        )
      : objects;

  // A run resolves from its ROOM, so the room is the frame to re-solve
  // against and the only question left is whether the run has segments.
  if (refit) {
    // A PINNED width has to move onto the new family's list too, or "re-fit
    // to its widths" is only half true: a 21" bay the user fixed under
    // Semi-Custom would sit in a Stock run that does not sell 21", and the
    // panel would be showing a width its own chip row cannot offer. On a line
    // with a size catalog it is the list for the bay's KIND (`snapBayWidthIn`),
    // read off the box the override governs — a 39" drawer base pinned under
    // Semi-Custom becomes Venus's 36" — or, where that box is not in the room,
    // a stand-in at the override's width and front. Tall overrides are exempt
    // — they pack as whole modules plus a filler, so any length is buildable
    // out of the family's boxes.
    const overrides = overridesForRun(newStored);
    const boxOf = (o) =>
      (room.objects || []).find(
        (obj) => obj.overrideId === o.id && obj.runId === runId
      ) || standInBoxFor(o, newStored);
    const snapped = overrides.map((o) =>
      !isTallOverride(o) && o.width > 0
        ? {
            ...o,
            width:
              snapBayWidthIn(newStored, boxOf(o), o.width / INCHES_TO_METERS, {
                front: o.type,
              }) * INCHES_TO_METERS,
          }
        : o
    );
    if (snapped.some((o, i) => o !== overrides[i])) {
      newStored.bayOverrides = snapped;
    }
    const roomForMat = { ...room, cabinetRuns };
    // Under a sized line the kept tall units take the line's pantry widths
    // BEFORE the solve, so their bays and the packing around them follow.
    const site = sizedLine
      ? {
          ...roomForMat,
          objects: tallUnitsInLine(roomForMat.objects, [newStored], family),
        }
      : roomForMat;
    const run = rehydrateRun(newStored, site);
    if (run.segments.length) {
      const out = materializeRunObjects(run, site, openings);
      const carried = carryObjects(
        objectsAfterRunResolve(site, runId, out.objects, out).map(stampFamily)
      );
      const objects = sizedLine
        ? runUnitsIntoLine(room, carried, {
            run: newStored,
            line: { family, companyOffering },
          })
        : carried;
      return { objects, cabinetRuns, family, refit: true };
    }
  }

  return {
    objects: carryObjects((room.objects || []).map(stampFamily)),
    cabinetRuns,
    family,
  };
}

/**
 * The fields a run DRAWN under the company's cabinet line is born with
 * (design 2026-10-05, D9) — the shared rule the kitchen generator also reads
 * (`@shared-lib/cabinet-runs` `familyFieldsForNewRun`).
 */
export const newRunFamilyFields = (room, cabinetLine, companyOffering) =>
  familyFieldsForNewRun(room, cabinetLine, companyOffering);

/**
 * The run-level records a family switch rewrites so the run wears what its
 * new family sells (`familyOffering.lookForFamily`): the run's own look —
 * or, for a run that inherits the ROOM's look, a look of its own when the
 * room's is not the new family's — and every bay override's look. Pure; the
 * fields to merge onto the stored run.
 */
export function lookCarriedIntoFamily(room, stored, family, companyOffering) {
  const offering = offeringOf(family, companyOffering);
  const out = {};
  const inherited = stored.appearance || room.cabinetAppearance || {};
  const mapped = lookForFamily(inherited, offering);
  if (stored.appearance || !sameLook(mapped, inherited))
    out.appearance = mapped;
  const overrides = overridesForRun(stored);
  if (overrides.some((o) => o.appearance)) {
    out.bayOverrides = overrides.map((o) =>
      o.appearance
        ? { ...o, appearance: lookForFamily(o.appearance, offering) }
        : o
    );
  }
  return out;
}

/**
 * Each of the run's CABINETS carried into the new family, door and finish.
 * The re-solve already dressed the boxes it re-emits; this reaches the ones
 * it keeps (a pinned tall unit, a carried corner) and is a no-op on the rest.
 * Countertops are not cabinets — their texture is the counter — so the
 * membership is the cabinetry one.
 */
function objectLooksCarriedIntoFamily(
  objects,
  newStored,
  family,
  companyOffering
) {
  const offering = offeringOf(family, companyOffering);
  const member = runCabinetryMembership(newStored);
  const runLook = newStored.appearance || null;
  return objects.map((o) => {
    if (!member(o)) return o;
    // A DOORLESS cabinet (filler, open shelf) has no door to carry and no
    // door to pick a default finish by: when the family does not sell its
    // finish, it takes the RUN's — the cabinets either side of it — not the
    // family default, which put a Pearl run's fillers in Champagne.
    const doorless = isDoorlessCabinet(o);
    const misses = doorless
      ? lookNotOffered(offering, o).filter((m) => m.kind !== 'door')
      : null;
    // Judged by its LOOK: glass the fill drew is not a door the family has to
    // sell, and stays over whatever look the family maps to (`keepGlass`).
    const asLook = { ...o, doorStyle: lookDoorStyleOf(o) };
    const mapped =
      doorless && misses.length && runLook
        ? { ...o, ...pickFinish(runLook) }
        : lookForFamily(asLook, offering);
    if (sameLook(mapped, asLook)) return o;
    const keys = doorless ? FINISH_KEYS : LOOK_KEYS;
    const look = Object.fromEntries(keys.map((k) => [k, mapped[k] ?? null]));
    return keepGlass(o, overlayLook(o, look));
  });
}

const FINISH_KEYS = ['paintColor', 'paintFinish', 'texture'];
const pickFinish = (look) =>
  Object.fromEntries(FINISH_KEYS.map((k) => [k, look[k] ?? null]));

/**
 * Pick the free-standing lower-cabinet nearest the edited bay's centre after a
 * re-solve (used for re-select after a freestanding edit, parallel to
 * pickReselectBay for wall-backed bays).
 */
export function pickReselectFreestandingBay(objects, bay) {
  const onSeg = (objects || []).filter(
    (o) => o.type === 'lower-cabinet' && o.freeSegIndex === bay?.freeSegIndex
  );
  if (!onSeg.length) return null;
  if (!bay?.center) return onSeg[0];
  const d2 = (o) =>
    (o.center.x - bay.center.x) ** 2 + (o.center.z - bay.center.z) ** 2;
  return onSeg.reduce((b, o) => (!b || d2(o) < d2(b) ? o : b), null);
}

/**
 * The object to keep selected once a re-solve has rewritten a run's objects.
 *
 * A run's generated pieces take their ids from where they START along the
 * wall, so any re-pack replaces them: the cabinet that was selected no longer
 * exists, the panel holds a deleted object, and every handle drawn off the
 * selection — a run's end handles among them — disappears (found 2026-10-22
 * after pulling a run's end: the selected 24¾" cabinet had become a 33" one).
 * The same id wins when it survived; else the nearest piece of the same kind
 * where the old one stood (`pickReselectBay`, `pickReselectFreestandingBay`).
 * Null only when there is nothing to fall back to.
 */
/**
 * The piece of a run's live PREVIEW — or of the run the drag just wrote — that
 * stands for `selected`, so the thing being dragged stays highlighted and
 * stays selected (user reports 2026-10-26: "it stops highlighting when we
 * drag", then "it ends up switching the highlight to the one that just got
 * created in its wake").
 *
 * A re-solve mints a run box's id from WHERE IT STARTS along its wall, so the
 * id is the worst thing to follow during a move: the box that moved has a new
 * one, and the box the run builds in the space it left starts where it did —
 * and is handed its old id. So a box the run owns is followed by what does
 * travel with it, first:
 *
 *  - the override the drag writes for it (`bayOverrideIdOf` — a width or move
 *    drag names its record after the box it started on);
 *  - its bay (a sink base);
 *  - only then the same id, for a box no drag has touched (a run-end drag).
 *
 * An appliance keeps its own id through a re-solve, so for one the id comes
 * first. Never by position: mid-drag the dragged box is exactly the one that
 * is NOT where it was.
 *
 * @returns {Object|null}
 */
export function dragCounterpart(objects, selected) {
  if (!selected || !objects?.length) return null;
  const sameType = (o) => o?.type === selected.type;
  const byId = () => objects.find((o) => o?.id === selected.id) || null;
  const byBay = () =>
    (selected.bayId &&
      objects.find((o) => sameType(o) && o.bayId === selected.bayId)) ||
    null;
  const byOverride = () => {
    const overrideId = bayOverrideIdOf(selected);
    return (
      (overrideId &&
        objects.find((o) => sameType(o) && o.overrideId === overrideId)) ||
      null
    );
  };
  if (!selected.runId) return byId() || byBay() || byOverride();
  return byOverride() || byBay() || byId();
}

export function successorAfterResolve(objects, previous) {
  if (!previous) return null;
  const same = (objects || []).find((o) => o?.id === previous.id);
  if (same) return same;
  if (previous.freeSegIndex != null) {
    return pickReselectFreestandingBay(objects, previous);
  }
  return pickReselectBay(objects, previous, previous.overrideId);
}

/**
 * Hand a VANITY to the run engine: the room's objects and `cabinetRuns` after
 * the vanity group `object` belongs to has been converted.
 *
 * Phase 3b's explicit half. The reprocess converts every scanned vanity on
 * its own (`bathroom-post-processing`'s last step), so this is for the vanity
 * a reprocess has not reached — one just built by a chat tool against an old
 * room doc, or one whose conversion a user undid — and for giving the action
 * a name in the UI.
 *
 * Non-destructive, like every derive: `runId` is stamped on the cabinet and
 * its counter and NOTHING moves. The first bay edit is what re-solves the
 * span.
 *
 * @returns {{objects, cabinetRuns, run}|null} null when this vanity cannot be
 *   converted (the reasons are `vanityToRun`'s warnings, logged there).
 */
export function convertVanityToRun(room, object) {
  const fixtureGroupId = object?.fixtureGroupId;
  if (!fixtureGroupId) return null;
  const { run, objects, warnings } = vanityToRun(room, {
    fixtureGroupId,
    source: 'scan',
  });
  if (!run) {
    for (const warning of warnings) {
      console.warn(`[CabinetRun] vanity not converted: ${warning}`);
    }
    return null;
  }
  return {
    objects,
    // The run replaces its own previous entry and nothing else — every other
    // run in the room is someone else's.
    cabinetRuns: [
      ...(room?.cabinetRuns || []).filter((r) => r?.id !== run.id),
      run,
    ],
    run,
  };
}

export { canConvertVanityToRun };

/**
 * The room after the run behind `runId` is DELETED: its remaining objects, the
 * ids that went, and the RESOLVED stored run.
 *
 * Not a re-solve and not a policy question — see `deleteRun.js`. The wrapper
 * exists so the delete site reaches the engine the same way every other web
 * site does, and so the "a run the room does not list throws" contract is the
 * one `storedRunFor` already enforces.
 *
 * The resolved `run` is returned because the CALLER still has work the id it
 * was given cannot do: `bay.runId` may be a per-wall ALIAS, so filtering
 * `cabinetRuns` by that string would drop nothing and leave a stored run with
 * no objects (the same reason `useRunDrag` / `useCabinetApplianceDrop` write
 * back against `stored.id` rather than the id they were handed).
 */
export function deleteRunFromRoom(room, runId) {
  const run = storedRunFor(
    room,
    runId,
    'deleteRunFromRoom',
    'and a delete has to read its bays'
  );
  return { ...objectsAfterRunDelete(room?.objects || [], run), run };
}

/** `objectsAfterRunResolve` for a batch of runs (scoped wall-move regen). */
export function objectsAfterRunsResolve(
  room,
  runIds,
  newObjects,
  resolves,
  ctx = {}
) {
  const set = runIds instanceof Set ? runIds : new Set(runIds || []);
  const withRun = (resolves || []).filter((r) => r?.run?.id);
  const covered = new Set(withRun.map((r) => r.run.id));
  // A run in the batch with no resolve (its regen produced nothing) still has
  // its objects swept.
  const bare = [...set]
    .filter((id) => !covered.has(id))
    .map((id) => ({
      run: storedRunFor(room, id, 'objectsAfterRunsResolve'),
      objects: [],
      bays: [],
      removedObjectIds: [],
    }));
  const batch = [...withRun, ...bare];
  const result = mergeResolvedRuns(
    room,
    { resolves: batch, emitted: newObjects || [] },
    RESOLVE_SITES.EDIT,
    { ...ctx, settleGroups: resolveGroupsAfterMove }
  );
  reportMergeDiagnostics(result);
  return withVanityRunAccessories(
    room,
    batch.map((r) => r.run),
    batch.reduce((objs, r) => stampRunReadback(objs, r.run), result.objects),
    room?.objects
  );
}
